import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import { profileTrips } from '../../data/profileTrips';
import {
  Activity,
  CreateActivityRequest,
  Trip,
  createTrip,
  createActivity,
  deleteTrip,
  deleteActivity,
  getActivities,
  getTripById,
  setActivityTravelMode,
  setTripVisibility,
  updateActivity,
  updateTrip,
} from '../../../services/trips/api/tripsApi';
import type {
  CreateTripRequest,
  TripDates,
  ActivityTravelLeg,
  TravelMode,
} from '../../../services/trips/api/tripsApi';
import { buildActiveTripSnapshot } from '../../../services/trips/activeTrip';
import { detectConflicts, hasConflict, ConflictActivity } from '../../../services/trips/conflicts';
import { formatBudget, normalizeStatus } from '../../../services/trips/utils/tripUtils';
import { formatTripDates, parseLocalDate, toNaiveDateTime } from '../../../services/trips/utils/dateUtils';
import { extractActivityUuid } from '../../../services/trips/utils/idUtils';
import { getTokensFromCache } from '../../../services/auth/utils/tokenCache';
import { useWebSocketContext } from '../../../services/websocket/context/WebSocketContext';
import ActionRequestCard from '../../components/chat/ActionRequestCard';
import ReactionBar from '../../components/social/ReactionBar';
import { Reaction, tripReaction } from '../../../services/engagement/api';
import ChatAttachment from '../../components/chat/ChatAttachment';
import { ActionRequest, ActionResolution, collectFallbackActivities } from '../../../services/trips/actionRequests';
import { WebSocketMessage } from '../../../services/websocket/hooks/useWebSocket';
import TripMapSidebar from '../../components/maps/TripMapSidebar';
import {
  LocationRecommendation,
  normalizeLocationRecommendations,
  searchPlacesByText,
  fetchPlaceDetailsFromGoogle,
  fetchPlaceGeometryFromGoogle,
  PlaceSearchResult,
} from '../../components/maps/googlePlacesService';
import { PlaceDetails } from '../../components/maps/placeDetails';
import FormattedChatMessage from '../../components/chat/FormattedChatMessage';
import TimeField, { isValidActivityTimeRange } from '../../components/forms/TimeField/TimeField';
import ShareTripModal from '../../components/trips/ShareTripModal';
import './TripDetailPage.css';

// Chat response timing. Cold starts (fresh chat) can push the first reply past a
// minute, so we reassure at the soft mark and only error at the hard deadline —
// never a false timeout while the reply is still coming.
const SOFT_RESPONSE_TIMEOUT_MS = 45_000;
const HARD_RESPONSE_TIMEOUT_MS = 180_000;

const trip = {
  id: 'portugal',
  title: 'Portugal Escape',
  status: 'PLANNED',
  dateRange: 'Oct 12 – 17',
  duration: '5 days',
  stops: ['Lisbon', 'Sintra', 'Porto'],
  budget: '$1,500',
  travelers: 2,
  imageUrl: 'https://images.unsplash.com/photo-1513735492246-483525079686?auto=format&fit=crop&w=300&q=80',
  description: '', // real description comes from the backend; empty hides the section
};

interface TripActivity {
  id: string;
  day: string;
  description: string;
  startTime: string;
  endTime: string;
  place?: string;
  placeId?: string; // real Google place_id (server location.place_id), for map details
  budget?: string;
  category: 'travel' | 'activity' | 'food';
  travelFromPrev?: ActivityTravelLeg; // inbound travel leg (T1.16)
}

type TripActivityDraft = Omit<TripActivity, 'id' | 'day'>;

// How the new stop is timed. 'time' = the user picks exact clock times.
// 'relative' = the user drops it before/after an existing stop and gives a
// duration; the server then computes real times from the neighbour + travel and
// shifts the rest of the day (add-only — editing keeps exact times).
type ActivityPlacement = {
  mode: 'time' | 'relative';
  anchorId: string;
  side: 'before' | 'after';
  durationMin: number;
};

const DEFAULT_PLACEMENT: ActivityPlacement = {
  mode: 'time',
  anchorId: '',
  side: 'after',
  durationMin: 60,
};

interface AgentResponse {
  intent: string;
  confidence: number;
  message: string;
  trip_draft?: Record<string, any>;
  locations_recommendations?: Array<Record<string, any>>;
  activities?: Array<Record<string, any>>;
  action_request?: ActionRequest;
}

interface ChatMessage {
  id: string;
  message: string;
  isUser: boolean;
  timestamp: Date;
  isLoading?: boolean;
  isError?: boolean;
  intent?: string;
  trip_draft?: Record<string, any>;
  locations_recommendations?: LocationRecommendation[];
  activities?: Array<Record<string, any>>;
  action_request?: ActionRequest;
  actionResolution?: ActionResolution;
  createdTripId?: string;
  actionResolutionNote?: string;
}

interface TripBasicsForm {
  title: string;
  destinations: string[];
  startDate: string;
  endDate: string;
  budget: string;
  budgetCurrency: string;
  adults: string;
  children: string;
  visibility: 'private' | 'followers' | 'public';
}

const TRIP_VISIBILITY_OPTIONS: Array<{ value: TripBasicsForm['visibility']; label: string }> = [
  { value: 'private', label: 'Private — only you and trip members' },
  { value: 'followers', label: 'Followers — people who follow you' },
  { value: 'public', label: 'Public — anyone signed in' },
];

const tripVisibilityToFormValue = (backendTrip: Trip | null): TripBasicsForm['visibility'] => {
  if (backendTrip?.visibility) return backendTrip.visibility;
  if (backendTrip?.effectiveVisibility) return backendTrip.effectiveVisibility;
  return 'followers';
};

const tripDays = ['MON 12', 'TUE 13', 'WED 14', 'THU 15', 'FRI 16'];
const userOwnedTripIds = new Set([
  'iceland',
  'portugal',
  'japan',
  'alps',
  'amsterdam',
  'barcelona',
  'sedona',
  'draft',
]);

const publicTripPhotos = [
  'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?auto=format&fit=crop&w=700&q=80',
  'https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?auto=format&fit=crop&w=700&q=80',
];

const budgetBreakdown = [
  { label: 'Airplane tickets', value: '$520' },
  { label: 'Hotels & stays', value: '$420' },
  { label: 'Food', value: '$260' },
  { label: 'Events', value: '$160' },
  { label: 'Places & transport', value: '$140' },
];

const mapBackendTripToDetail = (backendTrip: Trip, canEditTrip: boolean) => {
  const destinations = backendTrip.destinations?.length ? backendTrip.destinations : ['Trip route'];
  const budget = formatBudget(backendTrip.budget);
  const imageUrl = backendTrip.images?.find((image) => /^https?:\/\//.test(image)) || trip.imageUrl;

  return {
    ...trip,
    id: String(backendTrip.id),
    title: backendTrip.title,
    status: canEditTrip ? getDetailStatusLabel(backendTrip.status) : 'PUBLIC TRIP',
    dateRange: formatTripDates(backendTrip.dates),
    duration: getDetailTripDuration(backendTrip),
    stops: destinations,
    budget: budget || trip.budget,
    travelers: formatTravelers(backendTrip.travelers) || String(trip.travelers),
    imageUrl,
    description: backendTrip.description || trip.description,
    visibility: backendTrip.visibility,
    effectiveVisibility: backendTrip.effectiveVisibility,
  };
};

const getDetailStatusLabel = (status: string) => {
  const normalized = normalizeStatus(status);

  if (normalized === 'in_progress') return 'IN PROGRESS';
  if (normalized === 'completed') return 'COMPLETED';
  if (normalized === 'draft') return 'DRAFT';
  if (normalized === 'confirmed') return 'CONFIRMED';
  return 'PLANNED';
};

const getDetailTripDuration = (backendTrip: Trip) => {
  if (!backendTrip.dates?.startDate || !backendTrip.dates?.endDate) return trip.duration;

  const start = parseLocalDate(backendTrip.dates.startDate);
  const end = parseLocalDate(backendTrip.dates.endDate);
  const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  if (!Number.isFinite(diff) || diff <= 0) {
    return trip.duration;
  }

  return `${diff} ${diff === 1 ? 'day' : 'days'}`;
};

const emptyDraftBasics: TripBasicsForm = {
  title: '',
  destinations: [''],
  startDate: '',
  endDate: '',
  budget: '',
  budgetCurrency: 'USD',
  adults: '',
  children: '',
  visibility: 'followers',
};

const buildTripBasicsForm = (backendTrip: Trip | null): TripBasicsForm => ({
  title: backendTrip?.title || '',
  destinations: backendTrip?.destinations?.length ? backendTrip.destinations : [''],
  startDate: backendTrip?.dates?.startDate || '',
  endDate: backendTrip?.dates?.endDate || '',
  budget: getBudgetAmount(backendTrip?.budget),
  budgetCurrency: getBudgetCurrency(backendTrip?.budget),
  adults: String(backendTrip?.travelers?.adults ?? 1),
  children: String(backendTrip?.travelers?.children ?? 0),
  visibility: tripVisibilityToFormValue(backendTrip),
});

const formatDraftBudget = (basics: TripBasicsForm): string => {
  if (!basics.budget) return '—';

  const amount = Number(basics.budget);
  if (!Number.isFinite(amount) || amount <= 0) return '—';

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: basics.budgetCurrency || 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${basics.budgetCurrency || 'USD'} ${amount}`;
  }
};

const formatDraftDuration = (startDate: string, endDate: string): string => {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  if (!Number.isFinite(diff) || diff <= 0) {
    return '';
  }

  return `${diff} ${diff === 1 ? 'day' : 'days'}`;
};

const formatDraftDateSummary = (basics: TripBasicsForm): string => {
  if (!basics.startDate && !basics.endDate) return 'Dates not set';

  if (basics.startDate && basics.endDate) {
    const dates = formatTripDates(buildTripDates(basics.startDate, basics.endDate));
    const duration = formatDraftDuration(basics.startDate, basics.endDate);
    return duration ? `${dates} · ${duration}` : dates;
  }

  if (basics.startDate) {
    return `Starts ${formatTripDates(buildTripDates(basics.startDate, basics.startDate))}`;
  }

  return `Ends ${formatTripDates(buildTripDates(basics.endDate, basics.endDate))}`;
};

const formatDraftTravelers = (basics: TripBasicsForm): string => {
  if (!basics.adults && !basics.children) return '—';

  const adults = Number(basics.adults) || 0;
  const children = Number(basics.children) || 0;
  const total = adults + children;
  return total > 0 ? String(total) : '—';
};

const buildDisplayedTripFromBasics = (basics: TripBasicsForm, description: string) => {
  const stops = basics.destinations.map((destination) => destination.trim()).filter(Boolean);

  return {
    id: 'draft',
    title: basics.title.trim() || 'Untitled trip',
    status: 'DRAFT',
    dateRange: formatDraftDateSummary(basics),
    duration: '',
    stops,
    budget: formatDraftBudget(basics),
    travelers: formatDraftTravelers(basics),
    imageUrl: '',
    description: description.trim(),
  };
};

const formatTravelers = (travelers?: { adults: number; children: number }) => {
  if (!travelers) return null;
  const parts: string[] = [];
  if (travelers.adults) parts.push(`${travelers.adults} adult${travelers.adults === 1 ? '' : 's'}`);
  if (travelers.children) parts.push(`${travelers.children} child${travelers.children === 1 ? '' : 'ren'}`);
  return parts.join(', ') || '1 adult';
};

const getBudgetAmount = (budget: Trip['budget']) => {
  if (!budget) return '';
  if (typeof budget === 'object') return String(budget.amount || '');
  return String(budget);
};

const getBudgetCurrency = (budget: Trip['budget']) => {
  if (budget && typeof budget === 'object') return budget.currency || 'USD';
  return 'USD';
};

const buildTripDates = (startDate: string, endDate: string): TripDates => ({
  type: 'concrete',
  startDate,
  endDate,
});

const parseAgentResponse = (text: string): { response: AgentResponse | null; isError: boolean } => {
  if (!text || typeof text !== 'string') {
    return { response: null, isError: false };
  }

  const trimmed = text.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return { response: null, isError: false };
  }

  try {
    const parsed = JSON.parse(trimmed) as any;
    const response = parsed.response?.intent ? parsed.response : parsed.intent ? parsed : null;

    if (response?.intent && response.message !== undefined && response.confidence !== undefined) {
      return { response, isError: false };
    }
  } catch {
    return { response: null, isError: false };
  }

  return { response: null, isError: false };
};

const mapServerActivityToTripActivity = (activity: Activity, fallbackDay: string): TripActivity => {
  const date = activity.dateTime ? new Date(activity.dateTime) : null;
  const endDate = activity.endDateTime ? new Date(activity.endDateTime) : null;
  const startTime = date && !Number.isNaN(date.getTime())
    ? date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    : '09:00';
  // Prefer the activity's real end time; fall back to +1h from the start.
  const endReference = endDate && !Number.isNaN(endDate.getTime())
    ? endDate
    : (date && !Number.isNaN(date.getTime()) ? new Date(date.getTime() + 60 * 60 * 1000) : null);
  const endTime = endReference
    ? endReference.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    : '10:00';

  return {
    id: activity.id,
    day: date && !Number.isNaN(date.getTime()) ? formatActivityDay(date) : fallbackDay,
    description: activity.title,
    startTime,
    endTime,
    place: activity.location,
    placeId: activity.placeId,
    budget: formatBudget(activity.budget) || undefined,
    category: inferActivityCategory(activity.title, activity.location),
    travelFromPrev: activity.travelFromPrev,
  };
};

const inferActivityCategory = (title: string, location: string): TripActivity['category'] => {
  const text = `${title} ${location}`.toLowerCase();

  if (/(flight|train|bus|drive|taxi|transfer|airport)/.test(text)) return 'travel';
  if (/(dinner|lunch|breakfast|food|restaurant|cafe|coffee)/.test(text)) return 'food';
  return 'activity';
};

const formatActivityDay = (date: Date) =>
  `${date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()} ${date.getDate()}`;

const resolveNextSelectedDays = (
  clickedDay: string,
  currentDays: string[],
  allDayLabels: string[],
): string[] => {
  if (!allDayLabels.length) return clickedDay ? [clickedDay] : [];

  const clickedIdx = allDayLabels.indexOf(clickedDay);
  if (clickedIdx === -1) return [clickedDay];

  if (currentDays.length > 1 && currentDays.includes(clickedDay)) {
    return [clickedDay];
  }

  if (!currentDays.includes(clickedDay)) {
    if (currentDays.length === 1) {
      const anchorIdx = allDayLabels.indexOf(currentDays[0]);
      if (anchorIdx === -1) return [clickedDay];
      const start = Math.min(anchorIdx, clickedIdx);
      const end = Math.max(anchorIdx, clickedIdx);
      return allDayLabels.slice(start, end + 1);
    }
    return [clickedDay];
  }

  return currentDays;
};

const sortActivitiesByDayAndTime = <T extends { day: string; startTime: string }>(
  items: T[],
  dayOrder: string[],
): T[] => [...items].sort((left, right) => {
  const dayDiff = dayOrder.indexOf(left.day) - dayOrder.indexOf(right.day);
  if (dayDiff !== 0) return dayDiff;
  return left.startTime.localeCompare(right.startTime);
});

const getTripDayWeekdayLong = (dayLabel: string, tripStartDate?: string): string | null => {
  if (tripStartDate) {
    const start = parseLocalDate(tripStartDate);
    if (!Number.isNaN(start.getTime())) {
      const cursor = new Date(start);
      for (let index = 0; index < 21; index += 1) {
        if (formatActivityDay(cursor) === dayLabel) {
          return cursor.toLocaleDateString('en-US', { weekday: 'long' });
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    }
  }

  const weekdayShort = dayLabel.split(' ')[0]?.toUpperCase();
  const weekdayMap: Record<string, string> = {
    MON: 'Monday',
    TUE: 'Tuesday',
    WED: 'Wednesday',
    THU: 'Thursday',
    FRI: 'Friday',
    SAT: 'Saturday',
    SUN: 'Sunday',
  };
  return weekdayShort ? weekdayMap[weekdayShort] || null : null;
};

const getHoursForTripDay = (
  place: PlaceDetails,
  dayLabel: string,
  tripStartDate?: string,
): string | null => {
  const weekdayText = place.opening_hours?.weekday_text;
  const weekdayLong = getTripDayWeekdayLong(dayLabel, tripStartDate);
  if (!weekdayText?.length || !weekdayLong) return null;

  return weekdayText.find((row) => row.toLowerCase().startsWith(weekdayLong.toLowerCase())) ?? null;
};

const getTripDayLabels = (backendTrip: Trip | null, activities: TripActivity[]) => {
  if (backendTrip?.dates?.startDate && backendTrip?.dates?.endDate) {
    const start = parseLocalDate(backendTrip.dates.startDate);
    const end = parseLocalDate(backendTrip.dates.endDate);
    const labels: string[] = [];

    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const cursor = new Date(start);
      while (cursor <= end && labels.length < 21) {
        labels.push(formatActivityDay(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    if (labels.length > 0) return labels;
  }

  const activityDays = Array.from(new Set(activities.map((activity) => activity.day)));
  if (activityDays.length > 0) return activityDays;

  if (backendTrip?.dates?.startDate) {
    const start = parseLocalDate(backendTrip.dates.startDate);
    if (!Number.isNaN(start.getTime())) {
      return [formatActivityDay(start)];
    }
  }

  return [];
};

const getDefaultTripDay = (backendTrip: Trip | null) => {
  if (backendTrip?.dates?.startDate) {
    const start = parseLocalDate(backendTrip.dates.startDate);
    if (!Number.isNaN(start.getTime())) {
      return formatActivityDay(start);
    }
  }

  return '';
};

// "HH:MM" → minutes since midnight (0 for missing/garbage parts).
const hhmmToMinutes = (hhmm: string): number => {
  const [h, m] = (hhmm || '').split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

// minutes → "HH:MM", clamped inside a single day.
const minutesToHhmm = (min: number): string => {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, Math.round(min)));
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
};

const buildActivityDateTime = (day: string, time: string, backendTrip: Trip | null) => {
  const [weekday, dayNumber] = day.split(' ');

  if (backendTrip?.dates?.startDate) {
    const start = parseLocalDate(backendTrip.dates.startDate);
    const targetDay = Number(dayNumber);
    const cursor = new Date(start);

    for (let index = 0; index < 21; index += 1) {
      if (
        cursor.getDate() === targetDay &&
        cursor.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase() === weekday
      ) {
        const [hours, minutes] = time.split(':').map(Number);
        // Naive wall-clock (no toISOString UTC shift) — same format as the
        // agent/chat path so both write paths agree and render identically.
        return toNaiveDateTime(cursor, hours || 0, minutes || 0);
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  const fallback = new Date();
  const [hours, minutes] = time.split(':').map(Number);
  return toNaiveDateTime(fallback, hours || 0, minutes || 0);
};

const TripDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tripId } = useParams<{ tripId: string }>();
  const webSocket = useWebSocketContext();
  const isDraft = tripId === 'draft';
  const routeState = location.state as { backTo?: string; owned?: boolean; trip?: Trip } | null;
  const backTo = routeState?.backTo;
  const [serverTrip, setServerTrip] = React.useState<Trip | null>(routeState?.trip || null);
  const isKnownPublicProfileTrip = profileTrips.some((item) => item.id === tripId);
  // T2.3d: the server's viewerRole is authoritative when present — owner and
  // collaborators edit; members/buyers/viewers get the read-only view. The
  // legacy heuristics remain as fallback for responses without a role.
  const roleFromServer = serverTrip?.viewerRole;
  const canEditTrip = Boolean(
    isDraft
    || (roleFromServer
      ? roleFromServer === 'owner' || roleFromServer === 'collaborator'
      : (routeState?.owned
        || (tripId && userOwnedTripIds.has(tripId))
        || (serverTrip && tripId && !isKnownPublicProfileTrip))),
  );
  const isTripOwner = roleFromServer ? roleFromServer === 'owner' : canEditTrip;
  const [isShareOpen, setIsShareOpen] = React.useState(false);
  const currentUserId = React.useMemo(() => {
    try {
      const idToken = getTokensFromCache()?.idToken;
      if (!idToken) return undefined;
      const payload = JSON.parse(atob(idToken.split('.')[1] || ''));
      return payload?.sub as string | undefined;
    } catch {
      return undefined;
    }
  }, []);
  const [isLoadingTrip, setIsLoadingTrip] = React.useState(!isDraft && !routeState?.trip);
  const [tripError, setTripError] = React.useState<string | null>(null);
  const [isLoadingActivities, setIsLoadingActivities] = React.useState(false);
  const [activityError, setActivityError] = React.useState<string | null>(null);
  const [chatMessages, setChatMessages] = React.useState<ChatMessage[]>([]);
  const [selectedRecommendation, setSelectedRecommendation] = React.useState<LocationRecommendation | null>(null);
  const [currentInput, setCurrentInput] = React.useState('');
  const [isLoadingConversation, setIsLoadingConversation] = React.useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = React.useState(false);
  const [conversationId, setConversationId] = React.useState(routeState?.trip?.conversation_id || '');
  const chatMessagesEndRef = React.useRef<HTMLDivElement | null>(null);
  const responseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const hardResponseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const profileTrip = profileTrips.find((item) => item.id === tripId);
  const [activities, setActivities] = React.useState<TripActivity[]>([]);
  // Raw server activities (rich contract: ISO times, visitMinutes, travel legs,
  // structured cost) kept alongside the lossy display view above — the source
  // for the agent's active_trip snapshot (T1.9). Refreshed at every authoritative
  // load (initial fetch + post-edit reload), same moments as `activities`.
  const [serverActivities, setServerActivities] = React.useState<Activity[]>([]);
  const [tripDescription, setTripDescription] = React.useState('');
  const [tripBasics, setTripBasics] = React.useState<TripBasicsForm>(() =>
    isDraft ? emptyDraftBasics : buildTripBasicsForm(routeState?.trip || null),
  );
  const displayedTrip = React.useMemo(() => {
    if (serverTrip) {
      return mapBackendTripToDetail(serverTrip, canEditTrip);
    }

    if (isDraft) {
      return buildDisplayedTripFromBasics(tripBasics, tripDescription);
    }

    if (profileTrip) {
      return {
        ...trip,
        id: profileTrip.id,
        title: profileTrip.title,
        status: canEditTrip ? trip.status : 'PUBLIC TRIP',
        dateRange: profileTrip.meta.split(' · ')[0] || trip.dateRange,
        duration: profileTrip.meta.split(' · ')[1] || trip.duration,
        imageUrl: profileTrip.imageUrl,
        description: profileTrip.description,
      };
    }

    return trip;
  }, [serverTrip, canEditTrip, isDraft, tripBasics, tripDescription, profileTrip]);
  const [tripBasicsError, setTripBasicsError] = React.useState<string | null>(null);
  const [isSavingTripBasics, setIsSavingTripBasics] = React.useState(false);
  const [editingActivityId, setEditingActivityId] = React.useState<string | null>(null);
  const [selectedActivityId, setSelectedActivityId] = React.useState<string | null>(null);
  const tripDayLabels = React.useMemo(() => getTripDayLabels(serverTrip, activities), [serverTrip, activities]);
  const [selectedDays, setSelectedDays] = React.useState<string[]>([]);
  const [lastSelectedDay, setLastSelectedDay] = React.useState('');
  const [activityDraft, setActivityDraft] = React.useState<TripActivityDraft>(createEmptyActivityDraft());
  const [activityPlacement, setActivityPlacement] = React.useState<ActivityPlacement>(DEFAULT_PLACEMENT);
  const [isActivityModalOpen, setIsActivityModalOpen] = React.useState(false);
  // Edit-basics modal for an already-created trip (drafts edit inline instead).
  const [isEditingBasics, setIsEditingBasics] = React.useState(false);
  // In-app delete confirmation (replaces the native window.confirm).
  const [isConfirmingDelete, setIsConfirmingDelete] = React.useState(false);
  const [activityPendingDelete, setActivityPendingDelete] = React.useState<{ id: string; title: string } | null>(null);
  const [isDeletingActivity, setIsDeletingActivity] = React.useState(false);
  // A save held at the confirm gate: a same-time/overlap clash on ADD that the
  // user must order (before/after). The prepared payload waits here until the
  // user resolves it. (Time edits are non-blocking — no gate; see handleSaveActivity.)
  const [pendingSave, setPendingSave] = React.useState<
    | { kind: 'conflict'; data: CreateActivityRequest; anchor: ConflictActivity; othersCount: number }
    | null
  >(null);
  const [isSavingActivity, setIsSavingActivity] = React.useState(false);
  // Like/bookmark state for this trip (T2.14).
  const [reaction, setReaction] = React.useState<Reaction | undefined>(undefined);

  const tripUuid = React.useMemo(() => {
    if (!tripId || isDraft) return '';
    return tripId;
  }, [tripId, isDraft]);

  // Like/bookmark state (T2.14): trip reads arrive enriched with likes +
  // viewer state, so the reaction derives from the loaded trip — no extra
  // request (this also re-syncs on the post-confirm refetch).
  React.useEffect(() => {
    if (!tripUuid || !serverTrip) return;
    setReaction(tripReaction(serverTrip));
  }, [tripUuid, serverTrip]);

  const fetchConversation = React.useCallback(async (nextConversationId: string) => {
    const conversationsEndpoint = process.env.REACT_APP_APIGATEWAY_CONVERSATIONS_ENDPOINT;

    if (!conversationsEndpoint) return;

    setIsLoadingConversation(true);
    try {
      const tokens = getTokensFromCache();

      if (!tokens?.idToken) {
        throw new Error('User not authenticated');
      }

      let endpoint = conversationsEndpoint;
      if (endpoint.endsWith('/conversations')) {
        endpoint = endpoint.replace(/\/conversations$/, '/conversation');
      } else {
        endpoint = endpoint.replace(/\/$/, '') + '/conversation';
      }

      const response = await fetch(`${endpoint}?conversation_id=${encodeURIComponent(nextConversationId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch conversation: ${response.statusText}`);
      }

      const data = await response.json();
      const formattedMessages: ChatMessage[] = (data.messages || []).map((message: any) => {
        const parsed = message.actor === 'assistant' ? parseAgentResponse(message.text || '') : { response: null, isError: false };

        return {
          id: message.message_id?.toString() || `history-${Date.now()}-${Math.random()}`,
          message: parsed.response?.message || message.text || '',
          isUser: message.actor === 'user',
          timestamp: new Date((message.created_at || Date.now() / 1000) * 1000),
          intent: parsed.response?.intent,
          trip_draft: parsed.response?.trip_draft,
          locations_recommendations: normalizeLocationRecommendations(parsed.response?.locations_recommendations),
          activities: parsed.response?.activities,
        };
      });

      setChatMessages(formattedMessages);
    } catch (err: any) {
      setChatMessages([{
        id: `conversation-error-${Date.now()}`,
        message: `Could not load conversation history: ${err.message}`,
        isUser: false,
        timestamp: new Date(),
        isError: true,
      }]);
    } finally {
      setIsLoadingConversation(false);
    }
  }, []);

  React.useEffect(() => {
    if (!tripUuid) return;

    let isMounted = true;

    const fetchTrip = async () => {
      try {
        setIsLoadingTrip(true);
        setTripError(null);
        const fetchedTrip = await getTripById(tripUuid);

        if (!isMounted) return;

        setServerTrip(fetchedTrip);
        setTripDescription(fetchedTrip.description || '');

        if (fetchedTrip.conversation_id) {
          setConversationId(fetchedTrip.conversation_id);
          fetchConversation(fetchedTrip.conversation_id);
        }
      } catch (err: any) {
        if (isMounted) {
          setTripError(err.message || 'Failed to load trip.');
        }
      } finally {
        if (isMounted) {
          setIsLoadingTrip(false);
        }
      }
    };

    fetchTrip();

    return () => {
      isMounted = false;
    };
  }, [tripUuid, fetchConversation]);

  React.useEffect(() => {
    setTripBasics(buildTripBasicsForm(serverTrip));
  }, [serverTrip]);

  React.useEffect(() => {
    setSelectedActivityId(null);
    setSelectedRecommendation(null);
  }, [tripUuid]);

  React.useEffect(() => {
    if (!tripUuid) return;

    let isMounted = true;

    const fetchTripActivities = async () => {
      try {
        setIsLoadingActivities(true);
        setActivityError(null);
        const fetchedActivities = await getActivities(tripUuid);

        if (!isMounted) return;

        const fallbackDay = getDefaultTripDay(serverTrip);
        const mappedActivities = fetchedActivities.map((activity) => mapServerActivityToTripActivity(activity, fallbackDay));
        setActivities(mappedActivities);
        setServerActivities(fetchedActivities);
      } catch (err: any) {
        if (isMounted) {
          setActivityError(err.message || 'Failed to load activities.');
          setActivities([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingActivities(false);
        }
      }
    };

    fetchTripActivities();

    return () => {
      isMounted = false;
    };
  }, [tripUuid, serverTrip]);

  React.useEffect(() => {
    if (!tripDayLabels.length) {
      setSelectedDays([]);
      setLastSelectedDay('');
      return;
    }

    setSelectedDays((current) => {
      const valid = current.filter((day) => tripDayLabels.includes(day));
      if (valid.length > 0) return valid;
      return [tripDayLabels[0]];
    });
  }, [tripDayLabels]);

  React.useEffect(() => {
    if (!lastSelectedDay || !tripDayLabels.includes(lastSelectedDay)) {
      setLastSelectedDay(selectedDays[selectedDays.length - 1] || tripDayLabels[0] || '');
    }
  }, [lastSelectedDay, selectedDays, tripDayLabels]);

  React.useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isLoadingConversation]);

  const finishAssistantMessage = React.useCallback((
    message: string,
    isError = false,
    agentResponse?: AgentResponse
  ) => {
    setChatMessages((current) => {
      const updated = [...current];
      const lastMessageIndex = updated.length - 1;
      const messageData: Partial<ChatMessage> = {
        message,
        isLoading: false,
        isError,
        intent: agentResponse?.intent,
        trip_draft: agentResponse?.trip_draft,
        locations_recommendations: normalizeLocationRecommendations(agentResponse?.locations_recommendations),
        activities: agentResponse?.activities,
        action_request: agentResponse?.action_request,
      };

      if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
        updated[lastMessageIndex] = {
          ...updated[lastMessageIndex],
          ...messageData,
        };
        return updated;
      }

      return [
        ...updated,
        {
          id: `assistant-${Date.now()}`,
          message,
          isUser: false,
          timestamp: new Date(),
          isLoading: false,
          isError,
          intent: agentResponse?.intent,
          trip_draft: agentResponse?.trip_draft,
          locations_recommendations: normalizeLocationRecommendations(agentResponse?.locations_recommendations),
          activities: agentResponse?.activities,
          action_request: agentResponse?.action_request,
        },
      ];
    });

    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    if (hardResponseTimeoutRef.current) {
      clearTimeout(hardResponseTimeoutRef.current);
      hardResponseTimeoutRef.current = null;
    }
    setIsWaitingForResponse(false);
  }, []);

  React.useEffect(() => {
    if (!canEditTrip) return undefined;

    return webSocket.subscribe((message: WebSocketMessage) => {
      const incomingConversationId = message.conversation_id || (message as any).conversationId;

      if (incomingConversationId) {
        setConversationId(incomingConversationId);
      }

      if (conversationId && incomingConversationId && incomingConversationId !== conversationId) {
        return;
      }

      const status = (message as any).status || (message as any).body?.status || (message as any).response?.status;
      const text =
        (message as any).text ||
        (message as any).body?.text ||
        (message as any).response?.text ||
        (message as any).message ||
        (message as any).response?.message;
      const error = (message as any).error || (message as any).body?.error || (message as any).response?.error;

      if (status === 'processing' || message.message === 'Connection accepted') {
        return;
      }

      if (error) {
        finishAssistantMessage(`Error: ${error}`, true);
        return;
      }

      if (status === 'complete' || text) {
        let responseText = text || '';
        let agentResponse: AgentResponse | null = null;

        if ((message as any).response && typeof (message as any).response === 'object' && (message as any).response.intent) {
          agentResponse = (message as any).response as AgentResponse;
          responseText = agentResponse.message;
        } else {
          const parsed = parseAgentResponse(responseText);
          agentResponse = parsed.response;
          responseText = agentResponse?.message || responseText;
        }

        finishAssistantMessage(responseText, false, agentResponse || undefined);
      }
    });
  }, [canEditTrip, conversationId, finishAssistantMessage, webSocket]);

  React.useEffect(() => () => {
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
    }
    if (hardResponseTimeoutRef.current) {
      clearTimeout(hardResponseTimeoutRef.current);
    }
  }, []);

  const handleActivityDraftChange = (
    field: keyof TripActivityDraft,
    value: string
  ) => {
    setActivityDraft((current) => ({ ...current, [field]: value }));
  };

  const handleTripBasicsChange = (field: keyof TripBasicsForm, value: string | string[]) => {
    setTripBasics((current) => ({ ...current, [field]: value }));
    setTripBasicsError(null);
  };

  const handleDestinationChange = (index: number, value: string) => {
    const destinations = [...tripBasics.destinations];
    destinations[index] = value;
    handleTripBasicsChange('destinations', destinations);
  };

  const handleAddDestination = () => {
    handleTripBasicsChange('destinations', [...tripBasics.destinations, '']);
  };

  const handleRemoveDestination = (index: number) => {
    const nextDestinations = tripBasics.destinations.filter((_, currentIndex) => currentIndex !== index);
    handleTripBasicsChange('destinations', nextDestinations.length ? nextDestinations : ['']);
  };

  const validateTripBasics = () => {
    const destinations = tripBasics.destinations.filter((destination) => destination.trim());

    if (!tripBasics.title.trim()) return 'Trip title is required.';
    if (!destinations.length) return 'At least one destination is required.';
    if (!tripBasics.startDate || !tripBasics.endDate) return 'Trip dates are required.';
    if (parseLocalDate(tripBasics.startDate) > parseLocalDate(tripBasics.endDate)) return 'Start date must be before end date.';
    if (tripBasics.budget && Number(tripBasics.budget) < 0) return 'Budget must be greater than or equal to 0.';
    if (!Number.isInteger(Number(tripBasics.adults)) || Number(tripBasics.adults) < 1) return 'Adults must be a whole number of at least 1.';
    if (!Number.isInteger(Number(tripBasics.children)) || Number(tripBasics.children) < 0) return 'Children must be a whole number of 0 or more.';

    return null;
  };

  const handleSaveTripBasics = async () => {
    if (!canEditTrip) return;

    const validationError = validateTripBasics();
    if (validationError) {
      setTripBasicsError(validationError);
      return;
    }

    const budgetValue = Number(tripBasics.budget);
    const nextVisibility = tripBasics.visibility;
    const previousVisibility = tripVisibilityToFormValue(serverTrip);
    const tripPayload: CreateTripRequest = {
      title: tripBasics.title.trim(),
      destinations: tripBasics.destinations.filter((destination) => destination.trim()),
      dates: buildTripDates(tripBasics.startDate, tripBasics.endDate),
      status: serverTrip?.status === 'completed' ? 'completed' : 'planning',
      budget: Number.isFinite(budgetValue) && budgetValue > 0
        ? { amount: budgetValue, currency: tripBasics.budgetCurrency }
        : undefined,
      travelers: { adults: Number(tripBasics.adults), children: Number(tripBasics.children) },
      description: tripDescription || undefined,
    };

    try {
      setIsSavingTripBasics(true);
      setTripBasicsError(null);

      if (isDraft) {
        const createdTrip = await createTrip(tripPayload);
        const newTripId = String(createdTrip.id).replace('TRIP#', '');
        await setTripVisibility(newTripId, nextVisibility);
        setServerTrip({
          ...createdTrip,
          visibility: nextVisibility,
          effectiveVisibility: nextVisibility,
        });
        navigate(`/trips/${newTripId}`, {
          replace: true,
          state: { backTo: '/trips', owned: true, trip: createdTrip },
        });
      } else if (tripUuid) {
        const updatedTrip = await updateTrip({
          id: tripUuid,
          ...tripPayload,
        });
        let mergedTrip = updatedTrip;
        if (nextVisibility !== previousVisibility) {
          await setTripVisibility(tripUuid, nextVisibility);
          mergedTrip = {
            ...updatedTrip,
            visibility: nextVisibility,
            effectiveVisibility: nextVisibility,
          };
        }
        setServerTrip(mergedTrip);
        setIsEditingBasics(false);
      }
    } catch (err: any) {
      setTripBasicsError(err.message || 'Failed to save trip details.');
    } finally {
      setIsSavingTripBasics(false);
    }
  };

  const handleDeleteTrip = () => {
    if (!canEditTrip || !tripUuid || isDraft) return;
    setIsConfirmingDelete(true);
  };

  const confirmDeleteTrip = async () => {
    if (!canEditTrip || !tripUuid || isDraft) return;

    try {
      setIsSavingTripBasics(true);
      await deleteTrip(tripUuid);
      navigate('/trips', { replace: true });
    } catch (err: any) {
      setTripBasicsError(err.message || 'Failed to delete trip.');
      setIsConfirmingDelete(false);
    } finally {
      setIsSavingTripBasics(false);
    }
  };

  const handleSaveActivity = async () => {
    if (!canEditTrip || !tripUuid) return;

    // Description is optional — a picked/typed place names the stop (REQ-138).
    const title = activityDraft.description.trim() || (activityDraft.place || '').trim();
    if (!title) return;

    // Relative placement (add-only): the user dropped the stop before/after an
    // existing one instead of typing clock times. Seed its order right next to
    // the anchor and give it the chosen duration; the server then recomputes the
    // real times (anchor.depart + travel) and shifts the rest of the day — the
    // same mechanism the conflict picker uses. No conflict prompt here: the user
    // has already said exactly where it goes.
    if (!editingActivityId && activityPlacement.mode === 'relative') {
      const anchor = activities
        .filter((activity) => activity.day === lastSelectedDay)
        .find((activity) => activity.id === activityPlacement.anchorId);
      if (!anchor) return;
      const anchorStart = hhmmToMinutes(anchor.startTime);
      const seededStart = activityPlacement.side === 'after' ? anchorStart + 1 : anchorStart - 1;
      const relativeData: CreateActivityRequest = {
        title,
        location: activityDraft.place || title,
        placeId: activityDraft.placeId || undefined,
        dateTime: buildActivityDateTime(lastSelectedDay, minutesToHhmm(seededStart), serverTrip),
        endDateTime: buildActivityDateTime(
          lastSelectedDay,
          minutesToHhmm(seededStart + activityPlacement.durationMin),
          serverTrip,
        ),
        budget: parseActivityBudget(activityDraft.budget),
      };
      void commitActivitySave(relativeData);
      return;
    }

    // Time is NEVER a hard block — a time edit must always be saveable. Derive a
    // valid start/end from whatever the user left: fall back to the original (on
    // edit) or a sensible default for start, and if the end is missing or not
    // after the start, give it the original visit length (or 60 min). The modal
    // already showed an inline notice about any such adjustment.
    const originalEdited = editingActivityId
      ? activities.find((activity) => activity.id === editingActivityId)
      : undefined;
    const startTime = activityDraft.startTime || originalEdited?.startTime || '09:00';
    let endTime = activityDraft.endTime;
    if (!endTime || !isValidActivityTimeRange(startTime, endTime)) {
      const originalDuration = originalEdited
        ? hhmmToMinutes(originalEdited.endTime) - hhmmToMinutes(originalEdited.startTime)
        : 0;
      const durationMin = originalDuration > 0 ? originalDuration : 60;
      endTime = minutesToHhmm(Math.min(23 * 60 + 59, hhmmToMinutes(startTime) + durationMin));
    }

    const activityData: CreateActivityRequest = {
      title,
      location: activityDraft.place || title,
      // Real place_id from the picker → server stores {type:'place_id'} and
      // computes exact travel legs (Distance Matrix place_id: origins).
      placeId: activityDraft.placeId || undefined,
      dateTime: buildActivityDateTime(lastSelectedDay, startTime, serverTrip),
      endDateTime: buildActivityDateTime(lastSelectedDay, endTime, serverTrip),
      budget: parseActivityBudget(activityDraft.budget),
    };

    // Same-day stops in the conflict-check shape (times are "HH:MM" here).
    const sameDay: ConflictActivity[] = activities
      .filter((activity) => activity.day === lastSelectedDay)
      .map((activity) => ({
        id: activity.id,
        startTime: activity.startTime,
        endTime: activity.endTime,
        title: activity.description,
      }));

    if (editingActivityId) {
      // Editing a time re-sequences the whole day (arrive = prev.depart + travel),
      // so it almost always shifts the neighbours — but that's non-blocking now:
      // the modal already showed an inline notice, so just commit (no confirm gate,
      // never a disabled Save). The server re-times the day; reloadActivities pulls
      // the shifted schedule.
      void commitActivitySave(activityData);
      return;
    }

    // Adding a stop: if it clashes (same start, or overlaps/"during" another),
    // the day can't hold both at once — the scheduler will sequence them. Let the
    // user pick the order rather than resolving it arbitrarily.
    const conflicts = detectConflicts(
      { startTime, endTime },
      sameDay,
    );
    if (hasConflict(conflicts)) {
      const clashing = [...conflicts.sameStart, ...conflicts.overlap];
      setPendingSave({
        kind: 'conflict',
        data: activityData,
        anchor: clashing[0],
        othersCount: clashing.length - 1,
      });
      return;
    }

    void commitActivitySave(activityData);
  };

  // Persist the prepared activity (add or edit), then pull the server-recomputed
  // day. Shared by the direct path and the confirm-gate resolutions.
  const commitActivitySave = async (activityData: CreateActivityRequest) => {
    if (!tripUuid) return;
    setIsSavingActivity(true);
    try {
      if (editingActivityId) {
        const activityUuid = extractActivityUuid(editingActivityId);
        const updatedActivity = await updateActivity(tripUuid, activityUuid, activityData);
        const mappedActivity = mapServerActivityToTripActivity(updatedActivity, lastSelectedDay);
        setActivities((current) =>
          current.map((activity) => (activity.id === editingActivityId ? mappedActivity : activity))
        );
        setEditingActivityId(null);
      } else {
        const createdActivity = await createActivity(tripUuid, activityData);
        const mappedActivity = mapServerActivityToTripActivity(createdActivity, lastSelectedDay);
        setActivities((current) => [...current, mappedActivity]);
        setSelectedActivityId(mappedActivity.id);
      }

      setActivityDraft(createEmptyActivityDraft());
      setActivityPlacement(DEFAULT_PLACEMENT);
      setIsActivityModalOpen(false);
      setPendingSave(null);
      // Server re-timed the day (new/changed legs) — pull the recomputed schedule.
      void reloadActivities();
    } catch (err: any) {
      setActivityError(err.message || 'Failed to save activity.');
    } finally {
      setIsSavingActivity(false);
    }
  };

  // Resolve a same-time/overlap clash by seeding the new stop just before or
  // after the conflicting stop, so sort-by-start orders it as the user chose;
  // the server scheduler then computes the real sequenced times.
  const resolveConflictSave = (choice: 'before' | 'after') => {
    if (!pendingSave || pendingSave.kind !== 'conflict') return;
    const anchorStart = hhmmToMinutes(pendingSave.anchor.startTime);
    const duration = Math.max(
      15,
      hhmmToMinutes(activityDraft.endTime) - hhmmToMinutes(activityDraft.startTime),
    );
    const seededStart = choice === 'after' ? anchorStart + 1 : anchorStart - 1;
    const data: CreateActivityRequest = {
      ...pendingSave.data,
      dateTime: buildActivityDateTime(lastSelectedDay, minutesToHhmm(seededStart), serverTrip),
      endDateTime: buildActivityDateTime(lastSelectedDay, minutesToHhmm(seededStart + duration), serverTrip),
    };
    void commitActivitySave(data);
  };

  const handleAddActivity = () => {
    if (!canEditTrip) return;

    setEditingActivityId(null);
    setActivityDraft(createEmptyActivityDraft());
    setActivityPlacement(DEFAULT_PLACEMENT);
    setIsActivityModalOpen(true);
  };

  const handleEditActivity = (activity: TripActivity) => {
    if (!canEditTrip) return;

    setEditingActivityId(activity.id);
    setActivityDraft({
      description: activity.description,
      startTime: activity.startTime,
      endTime: activity.endTime,
      place: activity.place || '',
      placeId: activity.placeId,
      budget: activity.budget || '',
      category: activity.category,
    });
    // Relative placement is an add-time affordance; edits always use exact times.
    setActivityPlacement(DEFAULT_PLACEMENT);
    setIsActivityModalOpen(true);
  };

  const handleRemoveActivity = (activityId: string) => {
    if (!canEditTrip) return;

    const activity = activities.find((item) => item.id === activityId);
    setActivityPendingDelete({
      id: activityId,
      title: activity?.description || 'this activity',
    });
  };

  const confirmDeleteActivity = async () => {
    if (!canEditTrip || !tripUuid || !activityPendingDelete) return;

    const { id: activityId } = activityPendingDelete;

    try {
      setIsDeletingActivity(true);
      await deleteActivity(tripUuid, extractActivityUuid(activityId));
      setActivities((current) => current.filter((activity) => activity.id !== activityId));
      // Removing a stop re-stitches the day server-side — pull the new times.
      void reloadActivities();
      if (selectedActivityId === activityId) {
        setSelectedActivityId(null);
      }
      if (editingActivityId === activityId) {
        setEditingActivityId(null);
        setActivityDraft(createEmptyActivityDraft());
        setIsActivityModalOpen(false);
      }
      setActivityPendingDelete(null);
    } catch (err: any) {
      setActivityError(err.message || 'Failed to delete activity.');
    } finally {
      setIsDeletingActivity(false);
    }
  };

  // Reload the day's activities from the server. A structural edit (add/remove)
  // or a leg-mode change triggers a server-side reschedule that shifts multiple
  // downstream stops' times, so a local patch isn't enough — refetch the truth.
  const reloadActivities = React.useCallback(async () => {
    if (!tripUuid) return;
    try {
      const fetched = await getActivities(tripUuid);
      const fallbackDay = getDefaultTripDay(serverTrip);
      setActivities(fetched.map((activity) => mapServerActivityToTripActivity(activity, fallbackDay)));
      setServerActivities(fetched);
    } catch (err: any) {
      setActivityError(err.message || 'Failed to reload activities.');
    }
  }, [tripUuid, serverTrip]);

  // Change the transport mode for a stop's inbound leg (T1.16). The server
  // recomputes that leg from Google in the new mode and re-times the day.
  const handleChangeTravelMode = React.useCallback(async (activityId: string, mode: TravelMode) => {
    if (!canEditTrip || !tripUuid) return;
    await setActivityTravelMode(tripUuid, extractActivityUuid(activityId), mode);
    await reloadActivities();
  }, [canEditTrip, tripUuid, reloadActivities]);

  const handleCancelEdit = () => {
    setEditingActivityId(null);
    setActivityDraft(createEmptyActivityDraft());
    setActivityPlacement(DEFAULT_PLACEMENT);
    setIsActivityModalOpen(false);
    setPendingSave(null);
  };

  const handleClearMapPlace = () => {
    setSelectedActivityId(null);
    setSelectedRecommendation(null);
  };

  const handleSelectActivity = (activityId: string) => {
    setSelectedRecommendation(null);
    setSelectedActivityId(activityId);
  };

  const handleSelectDay = (day: string) => {
    setSelectedDays((current) => resolveNextSelectedDays(day, current, tripDayLabels));
    setLastSelectedDay(day);
    setSelectedRecommendation(null);
    setSelectedActivityId(null);
  };

  const handleOpenCalendar = () => {
    const startDate = serverTrip?.dates?.startDate || tripBasics.startDate;
    navigate('/calendar', startDate ? { state: { focusDate: startDate } } : undefined);
  };

  const handleDescriptionSave = async (nextDescription: string) => {
    setTripDescription(nextDescription);

    if (!canEditTrip || !tripUuid) return;

    try {
      const updatedTrip = await updateTrip({
        id: tripUuid,
        description: nextDescription,
      });
      setServerTrip(updatedTrip);
    } catch (err) {
      setTripDescription(displayedTrip.description);
    }
  };

  // T1.3: freeze the proposal card after the user acts; on confirm, refetch
  // the trip so activities/fields reflect the applied change immediately.
  const resolveActionRequest = React.useCallback((
    messageId: string,
    resolution: ActionResolution,
    note: string,
    createdTripId?: string,
  ) => {
    setChatMessages((current) => {
      const updated = current.map((message) => (
        message.id === messageId
          ? {
              ...message,
              actionResolution: resolution,
              createdTripId,
              actionResolutionNote: note,
            }
          : message
      ));

      if (resolution !== 'dismissed' && !createdTripId) {
        updated.push({
          id: `action-${resolution}-${Date.now()}`,
          message: resolution === 'confirmed' ? `✓ ${note}` : `⚠ ${note}`,
          isUser: false,
          timestamp: new Date(),
          isError: resolution === 'failed',
        });
      }

      return updated;
    });
    if (resolution === 'confirmed' && tripUuid) {
      getTripById(tripUuid)
        .then((freshTrip) => setServerTrip(freshTrip))
        .catch(() => undefined);
    }
  }, [tripUuid]);

  const handleSendChatMessage = async (message = currentInput) => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage || !canEditTrip || isWaitingForResponse) return;

    setCurrentInput('');
    setChatMessages((current) => [
      ...current,
      {
        id: `user-${Date.now()}`,
        message: trimmedMessage,
        isUser: true,
        timestamp: new Date(),
      },
      {
        id: `loading-${Date.now()}`,
        message: '',
        isUser: false,
        timestamp: new Date(),
        isLoading: true,
      },
    ]);
    setIsWaitingForResponse(true);

    try {
      await webSocket.sendMessage(trimmedMessage, conversationId || serverTrip?.conversation_id, {
        user_time: new Date().toISOString(),
        // T1.9/D-20: this is a SAVED trip the user is viewing, not a draft — push
        // it as active_trip (compact ground truth incl. the activities the agent
        // was previously blind to). Its id is the trip_id the agent uses for any
        // UPDATE_TRIP/ADD_ACTIVITIES/UPDATE_ACTIVITY it proposes.
        active_trip: serverTrip
          ? buildActiveTripSnapshot(serverTrip, serverActivities, { description: tripDescription })
          : undefined,
      });

      // Cold starts (fresh chat) can push the first reply past a minute — reassure
      // at the soft mark (keep loading) and only error at the hard deadline, so a
      // slow-but-coming reply lands on the loading bubble instead of following a
      // false "timed out" message.
      responseTimeoutRef.current = setTimeout(() => {
        setChatMessages((current) => {
          const updated = [...current];
          const i = updated.length - 1;
          if (i >= 0 && updated[i].isLoading) {
            updated[i] = { ...updated[i], message: 'Still working on it — the planner is warming up. This can take a little longer on the first message…' };
          }
          return updated;
        });
      }, SOFT_RESPONSE_TIMEOUT_MS);

      hardResponseTimeoutRef.current = setTimeout(() => {
        finishAssistantMessage('The planner is taking longer than expected. Please try again.', true);
      }, HARD_RESPONSE_TIMEOUT_MS);
    } catch (err: any) {
      finishAssistantMessage(err.message || 'Could not send message to the planner.', true);
    }
  };

  return (
    <DashboardLayout activeSection="trips" wide>
      <div className={`v2-trip-detail-page ${canEditTrip && !isDraft && !isLoadingTrip && !tripError ? 'v2-trip-detail-page--planner' : ''}`}>
        {isLoadingTrip && (
          <div className="v2-trip-detail-page__state">
            <span className="v2-trip-detail-page__spinner" />
            <p>Loading trip details...</p>
          </div>
        )}
        {!isLoadingTrip && tripError && (
          <div className="v2-trip-detail-page__state v2-trip-detail-page__state--error">
            <p>{tripError}</p>
            <button type="button" onClick={() => navigate(backTo || '/trips')}>Back to trips</button>
          </div>
        )}
        {!isLoadingTrip && !tripError && (
          <>
        <div className={`v2-trip-detail-page__workspace ${canEditTrip ? 'v2-trip-detail-page__workspace--owned' : 'v2-trip-detail-page__workspace--public'} ${isDraft ? 'v2-trip-detail-page__workspace--draft' : ''}`}>
          {canEditTrip ? (
            <>
              {isDraft ? (
                <>
                  <TripDetailHeader
                    displayedTrip={displayedTrip}
                    isDraft={isDraft}
                    canEditTrip={canEditTrip}
                    tripBasics={tripBasics}
                    tripUuid={tripUuid}
                    reaction={reaction}
                    budgetBreakdown={undefined}
                    onEditBasics={() => {
                      setTripBasics(buildTripBasicsForm(serverTrip));
                      setTripBasicsError(null);
                      setIsEditingBasics(true);
                    }}
                    onPrimaryAction={handleSaveTripBasics}
                    primaryActionLabel="Create this trip"
                  />
                  <div className="v2-trip-detail-page__setup">
                    <TripBasicsEditor
                      value={tripBasics}
                      error={tripBasicsError}
                      isSaving={isSavingTripBasics}
                      isDraft={isDraft}
                      onChange={handleTripBasicsChange}
                      onDestinationChange={handleDestinationChange}
                      onAddDestination={handleAddDestination}
                      onRemoveDestination={handleRemoveDestination}
                      onSave={handleSaveTripBasics}
                      onDelete={handleDeleteTrip}
                    />
                    <TripDescriptionEditor
                      value={tripDescription}
                      onChange={handleDescriptionSave}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="v2-trip-detail-page__main-column">
                    <TripDetailHeader
                      displayedTrip={displayedTrip}
                      isDraft={isDraft}
                      canEditTrip={canEditTrip}
                      tripBasics={tripBasics}
                      tripUuid={tripUuid}
                      reaction={reaction}
                      budgetBreakdown={serverTrip ? undefined : budgetBreakdown}
                      showDescription
                      onEditBasics={() => {
                        setTripBasics(buildTripBasicsForm(serverTrip));
                        setTripBasicsError(null);
                        setIsEditingBasics(true);
                      }}
                      onPrimaryAction={handleOpenCalendar}
                      primaryActionLabel="Full calendar"
                      onShare={() => setIsShareOpen(true)}
                    />

                    <main className="v2-trip-detail-page__itinerary" aria-label="Trip itinerary">
                      <DayPlan
                        activities={activities}
                        days={tripDayLabels}
                        selectedDays={selectedDays}
                        lastSelectedDay={lastSelectedDay}
                        selectedActivityId={selectedActivityId}
                        onAdd={handleAddActivity}
                        onSelectDay={handleSelectDay}
                        onSelectActivity={handleSelectActivity}
                        onEdit={handleEditActivity}
                        onRemove={handleRemoveActivity}
                        onChangeTravelMode={handleChangeTravelMode}
                        readOnly={false}
                        isLoading={isLoadingActivities}
                        error={activityError}
                      />
                    </main>
                  </div>

                  <aside className="v2-trip-detail-page__assistant" aria-label="Map and planning chat">
                    <TripMapSidebar
                      mapOnly
                      destinations={displayedTrip.stops}
                      activities={activities}
                      days={tripDayLabels}
                      selectedDays={selectedDays}
                      selectedActivityId={selectedActivityId}
                      onSelectActivity={handleSelectActivity}
                      onClearPlace={handleClearMapPlace}
                      onSelectDay={handleSelectDay}
                      selectedExternalPlaceId={selectedRecommendation?.place_id || selectedRecommendation?.name || null}
                      selectedExternalPlaceQuery={selectedRecommendation?.name}
                    />

                    <section className="v2-trip-chat v2-trip-chat--compact" aria-label="Planning chat">
                      <div className="v2-trip-chat__status">
                        <span aria-hidden>✣</span>
                        <h2>Planning with AI</h2>
                        <p>{webSocket.isConnected ? 'Connected' : webSocket.isConnecting ? 'Connecting' : 'Offline'}</p>
                      </div>

                      <div className="v2-trip-chat__messages">
                        {isLoadingConversation && <ChatStateMessage message="Loading conversation history..." />}
                        {!isLoadingConversation && chatMessages.length === 0 && (
                          <AssistantBubble>
                            Ask me to adjust this trip, add activities, refine the route, or update the budget.
                          </AssistantBubble>
                        )}
                        {chatMessages.map((message) => (
                          message.isUser ? (
                            <UserBubble key={message.id}>{message.message}</UserBubble>
                          ) : (
                            <React.Fragment key={message.id}>
                              <AssistantBubble isLoading={message.isLoading} isError={message.isError}>
                                {message.isLoading ? (message.message || 'Planning...') : message.message}
                              </AssistantBubble>
                              {!message.isLoading && message.action_request && (
                                <ChatAttachment>
                                  <ActionRequestCard
                                    request={message.action_request}
                                    resolution={message.actionResolution}
                                    createdTripId={message.createdTripId}
                                    resolutionNote={message.actionResolutionNote}
                                    backTo={backTo || `/trips/${tripUuid}`}
                                    conversationId={conversationId || serverTrip?.conversation_id || undefined}
                                    existingTripId={tripUuid || undefined}
                                    fallbackActivities={collectFallbackActivities(message)}
                                    onResolve={(resolution, note, tripId) => resolveActionRequest(message.id, resolution, note, tripId)}
                                  />
                                </ChatAttachment>
                              )}
                              {!message.isLoading && message.locations_recommendations && message.locations_recommendations.length > 0 && (
                                <div className="v2-trip-chat__recommendations">
                                  {message.locations_recommendations.map((recommendation) => (
                                    <button
                                      type="button"
                                      key={`${message.id}-${recommendation.place_id || recommendation.name}`}
                                      className={`v2-trip-chat__recommendation ${
                                        selectedRecommendation?.name === recommendation.name
                                        && selectedRecommendation?.place_id === recommendation.place_id
                                          ? 'v2-trip-chat__recommendation--active'
                                          : ''
                                      }`}
                                      onClick={() => {
                                        setSelectedActivityId(null);
                                        setSelectedRecommendation(recommendation);
                                      }}
                                    >
                                      {recommendation.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </React.Fragment>
                          )
                        ))}
                        <div ref={chatMessagesEndRef} />
                      </div>

                      <div className="v2-trip-chat__input">
                        <input
                          type="text"
                          value={currentInput}
                          onChange={(event) => setCurrentInput(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              handleSendChatMessage();
                            }
                          }}
                          placeholder="Message the planner..."
                          aria-label="Message the planner"
                          disabled={!webSocket.isConnected || isWaitingForResponse}
                        />
                        <button
                          type="button"
                          aria-label="Send message"
                          onClick={() => handleSendChatMessage()}
                          disabled={!webSocket.isConnected || isWaitingForResponse || !currentInput.trim()}
                        >
                          ↗
                        </button>
                      </div>
                    </section>
                  </aside>
                </>
              )}
            </>
          ) : (
            <>
              <TripDetailHeader
                displayedTrip={displayedTrip}
                isDraft={false}
                canEditTrip={false}
                tripBasics={tripBasics}
                tripUuid={tripUuid}
                reaction={reaction}
                budgetBreakdown={serverTrip ? undefined : budgetBreakdown}
                onEditBasics={() => undefined}
                onPrimaryAction={handleOpenCalendar}
                primaryActionLabel="Full calendar"
              />
              <PublicTripDetails
                description={displayedTrip.description}
                photos={[displayedTrip.imageUrl, ...publicTripPhotos.filter((photo) => photo !== displayedTrip.imageUrl)]}
              />

              <aside className="v2-trip-detail-sidebar" aria-label="Trip details">
                <TripMapSidebar
                  destinations={displayedTrip.stops}
                  activities={activities}
                  days={tripDayLabels}
                  selectedDays={selectedDays}
                  selectedActivityId={selectedActivityId}
                  onSelectActivity={setSelectedActivityId}
                  onSelectDay={handleSelectDay}
                />
                <DayPlan
                  activities={activities}
                  days={tripDayLabels}
                  selectedDays={selectedDays}
                  lastSelectedDay={lastSelectedDay}
                  selectedActivityId={selectedActivityId}
                  onAdd={handleAddActivity}
                  onSelectDay={handleSelectDay}
                  onSelectActivity={setSelectedActivityId}
                  onEdit={handleEditActivity}
                  onRemove={handleRemoveActivity}
                  readOnly
                  isLoading={isLoadingActivities}
                  error={activityError}
                />
              </aside>
            </>
          )}
        </div>

        {isActivityModalOpen && canEditTrip && (
          <ActivityEditorModal
            draft={activityDraft}
            editingActivityId={editingActivityId}
            destinations={displayedTrip.stops}
            dayActivities={activities.filter((activity) => activity.day === lastSelectedDay)}
            allActivities={activities}
            tripDayLabels={tripDayLabels}
            selectedDay={lastSelectedDay}
            tripStartDate={serverTrip?.dates?.startDate}
            placement={activityPlacement}
            onPlacementChange={setActivityPlacement}
            onDraftChange={handleActivityDraftChange}
            onSave={handleSaveActivity}
            onCancel={handleCancelEdit}
          />
        )}

        {isShareOpen && tripUuid && serverTrip && (
          <ShareTripModal
            tripId={tripUuid}
            viewerRole={serverTrip.viewerRole || (isTripOwner ? 'owner' : 'member')}
            currentUserId={currentUserId}
            visibility={serverTrip.visibility}
            effectiveVisibility={serverTrip.effectiveVisibility}
            onClose={() => setIsShareOpen(false)}
            onVisibilityChanged={(choice) => {
              setServerTrip((current) => current
                ? {
                  ...current,
                  visibility: choice === 'default' ? undefined : choice,
                  effectiveVisibility: choice === 'default' ? current.effectiveVisibility : choice,
                }
                : current);
            }}
            onLeft={() => {
              setIsShareOpen(false);
              navigate('/trips');
            }}
          />
        )}

        {isEditingBasics && canEditTrip && !isDraft && (
          <div className="v2-trip-activity-modal" role="dialog" aria-modal="true" aria-labelledby="trip-basics-modal-title">
            <div className="v2-trip-activity-modal__backdrop" onClick={() => setIsEditingBasics(false)} />
            <section className="v2-trip-activity-modal__panel">
              <header>
                <div>
                  <p>Trip details</p>
                  <h2 id="trip-basics-modal-title">Edit trip</h2>
                </div>
                <button type="button" onClick={() => setIsEditingBasics(false)} aria-label="Close trip editor">×</button>
              </header>
              <TripBasicsEditor
                value={tripBasics}
                error={tripBasicsError}
                isSaving={isSavingTripBasics}
                isDraft={isDraft}
                onChange={handleTripBasicsChange}
                onDestinationChange={handleDestinationChange}
                onAddDestination={handleAddDestination}
                onRemoveDestination={handleRemoveDestination}
                onSave={handleSaveTripBasics}
                onDelete={handleDeleteTrip}
              />
            </section>
          </div>
        )}

        {isConfirmingDelete && canEditTrip && !isDraft && (
          <div
            className="v2-trip-confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-trip-title"
            aria-describedby="delete-trip-desc"
          >
            <div
              className="v2-trip-confirm-modal__backdrop"
              onClick={() => { if (!isSavingTripBasics) setIsConfirmingDelete(false); }}
            />
            <section className="v2-trip-confirm-modal__panel">
              <h2 id="delete-trip-title" className="v2-trip-confirm-modal__title">Delete this trip?</h2>
              <p id="delete-trip-desc" className="v2-trip-confirm-modal__desc">
                This cannot be undone — the trip and all of its activities will be permanently
                deleted. It cannot be recreated.
              </p>
              <div className="v2-trip-confirm-modal__actions">
                <button
                  type="button"
                  className="v2-trip-confirm-modal__cancel"
                  onClick={() => setIsConfirmingDelete(false)}
                  disabled={isSavingTripBasics}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="v2-trip-confirm-modal__danger"
                  onClick={confirmDeleteTrip}
                  disabled={isSavingTripBasics}
                >
                  {isSavingTripBasics ? 'Deleting…' : 'Delete trip'}
                </button>
              </div>
            </section>
          </div>
        )}

        {activityPendingDelete && canEditTrip && !isDraft && (
          <div
            className="v2-trip-confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-activity-title"
            aria-describedby="delete-activity-desc"
          >
            <div
              className="v2-trip-confirm-modal__backdrop"
              onClick={() => { if (!isDeletingActivity) setActivityPendingDelete(null); }}
            />
            <section className="v2-trip-confirm-modal__panel">
              <h2 id="delete-activity-title" className="v2-trip-confirm-modal__title">Remove this activity?</h2>
              <p id="delete-activity-desc" className="v2-trip-confirm-modal__desc">
                <strong>{activityPendingDelete.title}</strong> will be permanently removed from your
                itinerary. This cannot be undone.
              </p>
              <div className="v2-trip-confirm-modal__actions">
                <button
                  type="button"
                  className="v2-trip-confirm-modal__cancel"
                  onClick={() => setActivityPendingDelete(null)}
                  disabled={isDeletingActivity}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="v2-trip-confirm-modal__danger"
                  onClick={confirmDeleteActivity}
                  disabled={isDeletingActivity}
                >
                  {isDeletingActivity ? 'Removing…' : 'Remove activity'}
                </button>
              </div>
            </section>
          </div>
        )}

        {/* Same-time / overlap clash on add: the user picks the order (no
            default — an explicit choice), which seeds the sequence. */}
        {pendingSave?.kind === 'conflict' && canEditTrip && (
          <div
            className="v2-trip-confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="conflict-title"
            aria-describedby="conflict-desc"
          >
            <div className="v2-trip-confirm-modal__backdrop" onClick={() => { if (!isSavingActivity) setPendingSave(null); }} />
            <section className="v2-trip-confirm-modal__panel">
              <h2 id="conflict-title" className="v2-trip-confirm-modal__title">Where should this stop go?</h2>
              <p id="conflict-desc" className="v2-trip-confirm-modal__desc">
                It clashes with <strong>{pendingSave.anchor.title || 'another stop'}</strong>
                {pendingSave.anchor.startTime ? ` (${pendingSave.anchor.startTime})` : ''}
                {pendingSave.othersCount > 0
                  ? ` and ${pendingSave.othersCount} other ${pendingSave.othersCount === 1 ? 'stop' : 'stops'}`
                  : ''}
                . Two stops can't share a slot, so pick the order — I'll re-time the day to fit.
              </p>
              <div className="v2-trip-confirm-modal__actions">
                <button
                  type="button"
                  className="v2-trip-confirm-modal__cancel"
                  onClick={() => setPendingSave(null)}
                  disabled={isSavingActivity}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="v2-trip-confirm-modal__cancel"
                  onClick={() => resolveConflictSave('before')}
                  disabled={isSavingActivity}
                >
                  Before it
                </button>
                <button
                  type="button"
                  className="v2-trip-confirm-modal__confirm"
                  onClick={() => resolveConflictSave('after')}
                  disabled={isSavingActivity}
                >
                  After it
                </button>
              </div>
            </section>
          </div>
        )}

        {/* Editing a time re-sequences the day — confirm the ripple first. */}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

interface TripDescriptionEditorProps {
  value: string;
  onChange: (value: string) => void;
}

interface TripBasicsEditorProps {
  value: TripBasicsForm;
  error: string | null;
  isSaving: boolean;
  isDraft: boolean;
  onChange: (field: keyof TripBasicsForm, value: string | string[]) => void;
  onDestinationChange: (index: number, value: string) => void;
  onAddDestination: () => void;
  onRemoveDestination: (index: number) => void;
  onSave: () => void;
  onDelete: () => void;
}

const TripBasicsEditor: React.FC<TripBasicsEditorProps> = ({
  value,
  error,
  isSaving,
  isDraft,
  onChange,
  onDestinationChange,
  onAddDestination,
  onRemoveDestination,
  onSave,
  onDelete,
}) => (
  <section className="v2-trip-basics-card" aria-labelledby="trip-basics-heading">
    <div className="v2-trip-basics-card__header">
      <div>
        <p>Trip setup</p>
        <h2 id="trip-basics-heading">Details</h2>
      </div>
      <div className="v2-trip-basics-card__actions">
        <button type="button" onClick={onSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : isDraft ? 'Create trip' : 'Save details'}
        </button>
        {!isDraft && (
          <button type="button" className="v2-trip-basics-card__danger" onClick={onDelete} disabled={isSaving}>
            Delete trip
          </button>
        )}
      </div>
    </div>

    <div className="v2-trip-basics-card__grid">
      <label className="v2-trip-basics-card__full">
        Trip title
        <input
          type="text"
          value={value.title}
          placeholder="Trip title"
          onChange={(event) => onChange('title', event.target.value)}
        />
      </label>
      <label>
        Start date
        <input
          type="date"
          value={value.startDate}
          onChange={(event) => onChange('startDate', event.target.value)}
        />
      </label>
      <label>
        End date
        <input
          type="date"
          value={value.endDate}
          onChange={(event) => onChange('endDate', event.target.value)}
        />
      </label>
      <label>
        Budget
        <input
          type="number"
          min="0"
          value={value.budget}
          placeholder="1500"
          onChange={(event) => onChange('budget', event.target.value)}
        />
      </label>
      <label>
        Currency
        <select value={value.budgetCurrency} onChange={(event) => onChange('budgetCurrency', event.target.value)}>
          <option value="USD">USD ($)</option>
          <option value="EUR">EUR (€)</option>
          <option value="GBP">GBP (£)</option>
          <option value="JPY">JPY (¥)</option>
          <option value="CAD">CAD ($)</option>
          <option value="AUD">AUD ($)</option>
        </select>
      </label>
      <label>
        Adults
        <input
          type="number"
          min="1"
          step="1"
          value={value.adults}
          onChange={(event) => onChange('adults', event.target.value)}
        />
      </label>
      <label>
        Children
        <input
          type="number"
          min="0"
          step="1"
          value={value.children}
          onChange={(event) => onChange('children', event.target.value)}
        />
      </label>
      <label className="v2-trip-basics-card__full">
        Privacy
        <select
          value={value.visibility}
          onChange={(event) => onChange('visibility', event.target.value)}
        >
          {TRIP_VISIBILITY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <div className="v2-trip-basics-card__destinations">
        <span>Destinations</span>
        {value.destinations.map((destination, index) => (
          <div key={`${index}-${destination}`}>
            <input
              type="text"
              value={destination}
              placeholder="Destination"
              onChange={(event) => onDestinationChange(index, event.target.value)}
            />
            <button type="button" onClick={() => onRemoveDestination(index)} aria-label="Remove destination">
              ×
            </button>
          </div>
        ))}
        <button type="button" onClick={onAddDestination}>+ Add destination</button>
      </div>
    </div>
    {error && <p className="v2-trip-basics-card__error">{error}</p>}
  </section>
);

const TripDescriptionEditor: React.FC<TripDescriptionEditorProps> = ({ value, onChange }) => {
  const [draft, setDraft] = React.useState(value);

  const handleSave = () => {
    onChange(draft.trim());
  };

  const handleCancel = () => {
    setDraft(value);
  };

  return (
    <section className="v2-trip-description-card" aria-labelledby="trip-description-heading">
      <div className="v2-trip-description-card__header">
        <div>
          <p>Trip story</p>
          <h2 id="trip-description-heading">Description</h2>
        </div>
        <div>
          <button type="button" onClick={handleCancel}>Cancel</button>
          <button type="button" onClick={handleSave}>Save</button>
        </div>
      </div>
      <textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Tell people what makes this trip special..."
        rows={4}
      />
    </section>
  );
};

interface PublicTripDetailsProps {
  description: string;
  photos: string[];
}

const PublicTripDetails: React.FC<PublicTripDetailsProps> = ({
  description,
  photos,
}) => (
  <main className="v2-trip-public-details" aria-label="Trip details and photos">
    <section>
      <p>Trip story</p>
      <p className="v2-trip-public-details__description">{description}</p>
    </section>

    <section>
      <p>Photos</p>
      <div className="v2-trip-public-details__photos">
        {photos.slice(0, 4).map((photo) => (
          <img key={photo} src={photo} alt="" />
        ))}
      </div>
    </section>
  </main>
);

const TRIP_TITLE_MAX_LENGTH = 50;

const truncateTripTitle = (title: string): string => {
  if (title.length <= TRIP_TITLE_MAX_LENGTH) return title;
  return `${title.slice(0, TRIP_TITLE_MAX_LENGTH)}...`;
};

interface TripDetailHeaderProps {
  displayedTrip: {
    title: string;
    status: string;
    dateRange: string;
    duration: string;
    stops: string[];
    budget: string;
    travelers: string | number;
    imageUrl: string;
    description?: string;
  };
  isDraft: boolean;
  canEditTrip: boolean;
  tripBasics: TripBasicsForm;
  tripUuid: string;
  reaction?: Reaction;
  budgetBreakdown?: Array<{ label: string; value: string }>;
  showDescription?: boolean;
  onEditBasics: () => void;
  onPrimaryAction: () => void;
  primaryActionLabel: string;
  onShare?: () => void;
}

const TripDetailHeader: React.FC<TripDetailHeaderProps> = ({
  displayedTrip,
  isDraft,
  canEditTrip,
  tripBasics,
  tripUuid,
  reaction,
  budgetBreakdown,
  showDescription = false,
  onEditBasics,
  onPrimaryAction,
  primaryActionLabel,
  onShare,
}) => (
  <header className="v2-trip-detail-page__header">
    <div className="v2-trip-detail-page__header-main">
      {!isDraft && (
        <img src={displayedTrip.imageUrl} alt="" className="v2-trip-detail-page__thumb" />
      )}
      {isDraft && <div className="v2-trip-detail-page__thumb v2-trip-detail-page__thumb--empty" aria-hidden />}
      <div className="v2-trip-detail-page__header-body">
        <div className="v2-trip-detail-page__header-row v2-trip-detail-page__header-row--top">
          <div className="v2-trip-detail-page__header-identity">
            <h1>
              <span
                className={`v2-trip-detail-page__title-text ${isDraft && !tripBasics.title.trim() ? 'v2-trip-detail-page__title--placeholder' : ''}`}
                title={displayedTrip.title}
              >
                {truncateTripTitle(displayedTrip.title)}
              </span>
              <span className={`v2-trip-detail-page__status ${isDraft ? 'v2-trip-detail-page__status--draft' : ''}`}>
                {isDraft ? 'DRAFT · NOT CREATED' : displayedTrip.status}
              </span>
              {!isDraft && (displayedTrip as any).effectiveVisibility && (
                <span
                  className="v2-trip-detail-page__visibility"
                  title={(displayedTrip as any).visibility
                    ? 'Visibility set on this trip'
                    : 'Follows your account setting'}
                >
                  {((displayedTrip as any).visibility || (displayedTrip as any).effectiveVisibility) === 'public'
                    ? '🌐 Public'
                    : ((displayedTrip as any).visibility || (displayedTrip as any).effectiveVisibility) === 'followers'
                      ? '👥 Followers'
                      : '🔒 Private'}
                </span>
              )}
              {canEditTrip && !isDraft && (
                <button
                  type="button"
                  className="v2-trip-detail-page__edit-basics"
                  onClick={onEditBasics}
                  aria-label="Edit trip details"
                  title="Edit trip details"
                >
                  ✎
                </button>
              )}
            </h1>
          </div>
        </div>

        <div className="v2-trip-detail-page__meta">
          <span className="v2-trip-detail-page__meta-dates">
            {isDraft ? displayedTrip.dateRange : `${displayedTrip.dateRange} · ${displayedTrip.duration}`}
          </span>
          <div className="v2-trip-detail-page__meta-stats">
            <MetricInline label="Budget" value={displayedTrip.budget} breakdown={isDraft ? undefined : budgetBreakdown} />
            <MetricInline
              label="Travelers"
              value={isDraft ? formatDraftTravelers(tripBasics) : String(displayedTrip.travelers)}
            />
            <MetricInline
              label="Stops"
              value={isDraft && displayedTrip.stops.length === 0 ? '—' : String(displayedTrip.stops.length)}
            />
          </div>
        </div>

        {showDescription && displayedTrip.description?.trim() && (
          <p className="v2-trip-detail-page__summary">{displayedTrip.description}</p>
        )}

        <div className="v2-trip-detail-page__header-row v2-trip-detail-page__header-row--bottom">
          {displayedTrip.stops.length > 0 && (
            <div className="v2-trip-detail-page__chips">
              {displayedTrip.stops.map((stop) => (
                <span key={stop}>{stop}</span>
              ))}
            </div>
          )}

          <div className="v2-trip-detail-page__toolbar">
            {!isDraft && tripUuid && (
              <ReactionBar tripId={tripUuid} reaction={reaction} />
            )}
            {canEditTrip && (
              <>
                <button type="button" className="v2-trip-detail-page__icon-btn" aria-label="Share trip" onClick={onShare}>
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                    <path
                      d="M12 3v12M8 7l4-4 4 4M5 14v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  className="v2-trip-detail-page__icon-btn v2-trip-detail-page__icon-btn--primary"
                  onClick={onPrimaryAction}
                  aria-label={primaryActionLabel}
                  title={primaryActionLabel}
                >
                  {isDraft ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                      <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                      <rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                      <path d="M3 10h18M8 3v4M16 3v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  </header>
);

interface MetricInlineProps {
  label: string;
  value: string;
  breakdown?: Array<{ label: string; value: string }>;
}

const MetricInline: React.FC<MetricInlineProps> = ({ label, value, breakdown }) => (
  <span className="v2-trip-detail-page__meta-item" tabIndex={breakdown ? 0 : undefined}>
    <span className="v2-trip-detail-page__meta-label">{label}</span>
    <strong>{value}</strong>
    {breakdown && (
      <span className="v2-trip-detail-page__budget-popover" role="tooltip">
        <span className="v2-trip-detail-page__budget-popover-title">Budget breakdown</span>
        {breakdown.map((item) => (
          <span key={item.label} className="v2-trip-detail-page__budget-popover-row">
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </span>
        ))}
      </span>
    )}
  </span>
);

interface BubbleProps {
  children: React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
}

const AssistantBubble: React.FC<BubbleProps> = ({ children, isLoading = false, isError = false }) => {
  const messageText = typeof children === 'string' ? children : null;

  return (
    <div className={`v2-trip-chat__row v2-trip-chat__row--assistant ${isError ? 'v2-trip-chat__row--error' : ''} ${isLoading ? 'v2-trip-chat__row--loading' : ''}`}>
      <span className="v2-trip-chat__spark">✦</span>
      {messageText && !isLoading ? (
        <FormattedChatMessage text={messageText} className="v2-trip-chat__bubble" />
      ) : (
        <p>{children}</p>
      )}
    </div>
  );
};

const UserBubble: React.FC<BubbleProps> = ({ children }) => (
  <div className="v2-trip-chat__row v2-trip-chat__row--user">
    <p>{children}</p>
  </div>
);

const ChatStateMessage: React.FC<{ message: string }> = ({ message }) => (
  <div className="v2-trip-chat__state">
    {message}
  </div>
);

// The travel connector between two consecutive stops (T1.16): vertical dotted
// rail on the left, then the leg's time + mode and (when editable) a mode picker.
const TRAVEL_MODE_LABEL: Record<string, string> = { drive: 'Drive', walk: 'Walk', transit: 'Transit' };
const TRAVEL_MODES: TravelMode[] = ['drive', 'walk', 'transit'];

const TravelLeg: React.FC<{
  leg: ActivityTravelLeg;
  readOnly?: boolean;
  onChangeMode?: (mode: TravelMode) => Promise<void>;
}> = ({ leg, readOnly, onChangeMode }) => {
  const [editing, setEditing] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const label = TRAVEL_MODE_LABEL[leg.mode] || leg.mode;
  const fmtMin = (min: number) => (min >= 60 ? `${Math.floor(min / 60)} h ${min % 60} min`.replace(' 0 min', '') : `${min} min`);
  const summary = leg.minutes != null ? `≈ ${fmtMin(leg.minutes)} · ${label}` : `No ${label.toLowerCase()} route`;
  const distance = leg.km != null ? ` · ${leg.km} km` : '';
  // Route sanity (REQ-138): unresolved beats the softer server flags.
  const warningText = leg.minutes == null
    ? "Can't calculate a route to this stop"
    : leg.warning === 'far'
      ? 'Very far from the previous stop'
      : leg.warning === 'detour'
        ? 'Big detour from your route'
        : null;
  const warningSeverity = leg.minutes == null ? 'error' : 'warn';
  // A cross-day leg is advisory: the drive across a day boundary. Inbound legs
  // (top of a day) note where they came "from"; the end-of-day outbound leg
  // notes where it goes "to" tomorrow's first stop.
  const fromNote = leg.crossDay && leg.fromLabel ? ` · from ${leg.fromLabel}` : '';
  const toNote = leg.crossDay && leg.toLabel ? ` · to ${leg.toLabel}` : '';

  const pick = async (mode: TravelMode) => {
    if (!onChangeMode || busy) return;
    setBusy(true);
    try {
      await onChangeMode(mode);
      setEditing(false);
    } catch {
      /* surfaced by the page-level error state */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`v2-trip-leg ${leg.crossDay ? 'v2-trip-leg--cross' : ''} ${warningText ? `v2-trip-leg--${warningSeverity}` : ''}`}>
      <span className="v2-trip-leg__rail" aria-hidden="true" />
      <div className="v2-trip-leg__body">
        {!editing ? (
          <>
            <span className="v2-trip-leg__summary">{summary}{distance}{fromNote}{toNote}</span>
            {warningText && (
              <span className={`v2-trip-leg__warning v2-trip-leg__warning--${warningSeverity}`} role="note">
                ⚠ {warningText}
              </span>
            )}
            {!readOnly && onChangeMode && (
              <button type="button" className="v2-trip-leg__edit" onClick={() => setEditing(true)}>Edit</button>
            )}
          </>
        ) : (
          <div className="v2-trip-leg__modes" role="group" aria-label="Travel mode">
            {TRAVEL_MODES.map((m) => (
              <button
                key={m}
                type="button"
                disabled={busy}
                className={`v2-trip-leg__mode ${leg.chosenMode === m || (!leg.chosenMode && leg.mode === m) ? 'v2-trip-leg__mode--active' : ''}`}
                onClick={() => pick(m)}
              >
                {TRAVEL_MODE_LABEL[m]}
              </button>
            ))}
            <button type="button" className="v2-trip-leg__cancel" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
};

interface DayPlanProps {
  activities: TripActivity[];
  days: string[];
  selectedDays: string[];
  lastSelectedDay: string;
  selectedActivityId: string | null;
  onAdd: () => void;
  onSelectDay: (day: string) => void;
  onSelectActivity: (activityId: string) => void;
  onEdit: (activity: TripActivity) => void;
  onRemove: (activityId: string) => void;
  onChangeTravelMode?: (activityId: string, mode: TravelMode) => Promise<void>;
  readOnly?: boolean;
  isLoading?: boolean;
  error?: string | null;
}

const DayPlan: React.FC<DayPlanProps> = ({
  activities,
  days,
  selectedDays,
  lastSelectedDay,
  selectedActivityId,
  onAdd,
  onSelectDay,
  onSelectActivity,
  onEdit,
  onRemove,
  onChangeTravelMode,
  readOnly = false,
  isLoading = false,
  error = null,
}) => {
  const sortedActivities = sortActivitiesByDayAndTime(
    activities.filter((activity) => selectedDays.includes(activity.day)),
    days,
  );
  const daySpend = sortedActivities.reduce((total, activity) => total + parseBudget(activity.budget), 0);
  const isMultiDay = selectedDays.length > 1;

  const lastSelectedDayIndex = days.indexOf(lastSelectedDay);
  const nextStop = !isMultiDay && lastSelectedDayIndex >= 0
    ? activities
        .filter((a) => days.indexOf(a.day) > lastSelectedDayIndex)
        .sort((a, b) => (days.indexOf(a.day) - days.indexOf(b.day)) || a.startTime.localeCompare(b.startTime))[0]
    : undefined;
  const outboundLeg: ActivityTravelLeg | null =
    sortedActivities.length > 0 && nextStop?.travelFromPrev
      ? { ...nextStop.travelFromPrev, crossDay: true, fromLabel: undefined, toLabel: nextStop.description }
      : null;

  const countLabel = isMultiDay
    ? `${sortedActivities.length} places · ${selectedDays.length} days`
    : `${sortedActivities.length} places scheduled`;

  if (!days.length) {
    return (
      <section className="v2-trip-day-card">
        <div className="v2-trip-day-card__empty">Add trip dates to start building your day plan.</div>
      </section>
    );
  }

  return (
    <section className="v2-trip-day-card">
      <div className="v2-trip-day-card__days" aria-label="Trip days">
        {days.map((day) => {
          const [weekday, dayNumber] = day.split(' ');
          return (
            <button
              type="button"
              key={day}
              className={selectedDays.includes(day) ? 'v2-trip-day-card__day--active' : undefined}
              onClick={() => onSelectDay(day)}
            >
              <span className="v2-trip-day-card__day-label">
                <span>{weekday}</span>
                <strong>{dayNumber}</strong>
              </span>
            </button>
          );
        })}
      </div>

      <div className="v2-trip-day-card__summary">
        <p className="v2-trip-day-card__count">{countLabel}</p>
        <div className="v2-trip-day-card__summary-actions">
          <div className="v2-trip-day-card__spend">
            <span>{isMultiDay ? 'Total spend' : 'Day spend'}</span>
            <strong>${daySpend}</strong>
          </div>
          {!readOnly && <button type="button" onClick={onAdd}>+ Add activity</button>}
        </div>
      </div>

      <div className="v2-trip-day-card__timeline">
        {isLoading && (
          <div className="v2-trip-day-card__empty">Loading activities...</div>
        )}
        {!isLoading && error && (
          <div className="v2-trip-day-card__empty">{error}</div>
        )}
        {!isLoading && !error && sortedActivities.map((activity, idx) => {
          const prevDay = idx > 0 ? sortedActivities[idx - 1].day : null;
          const showDayHeader = isMultiDay && activity.day !== prevDay;

          return (
          <React.Fragment key={activity.id}>
            {showDayHeader && (
              <div className="v2-trip-day-card__day-divider">{activity.day}</div>
            )}
            {activity.travelFromPrev && (
              <TravelLeg
                leg={activity.travelFromPrev}
                readOnly={readOnly}
                onChangeMode={onChangeTravelMode ? (mode) => onChangeTravelMode(activity.id, mode) : undefined}
              />
            )}
          <article
            className={`v2-trip-day-card__item ${activity.id === selectedActivityId ? 'v2-trip-day-card__item--selected' : ''}`}
            onClick={() => onSelectActivity(activity.id)}
          >
            <time>{activity.startTime}</time>
            <span className={`v2-trip-day-card__dot v2-trip-day-card__dot--${activity.category}`} />
            <div>
              <h3>{activity.description}</h3>
              <p>
                {activity.startTime}–{activity.endTime}
                {activity.place ? ` · ${activity.place}` : ''}
              </p>
            </div>
            <strong>{activity.budget || '—'}</strong>
            {!readOnly && (
              <div className="v2-trip-day-card__item-actions">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(activity);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove(activity.id);
                  }}
                >
                  Remove
                </button>
              </div>
            )}
          </article>
          </React.Fragment>
          );
        })}
        {/* End-of-day leg: the onward drive to the next day's first stop. */}
        {!isLoading && !error && outboundLeg && nextStop && (
          <TravelLeg
            leg={outboundLeg}
            readOnly={readOnly}
            onChangeMode={onChangeTravelMode ? (mode) => onChangeTravelMode(nextStop.id, mode) : undefined}
          />
        )}
        {!isLoading && !error && sortedActivities.length === 0 && (
          <div className="v2-trip-day-card__empty">
            {readOnly ? 'No public activities for the selected days.' : 'No activities yet. Add one for this day.'}
          </div>
        )}
      </div>
    </section>
  );
};

interface ActivityEditorModalProps {
  draft: TripActivityDraft;
  editingActivityId: string | null;
  destinations: string[];
  dayActivities: TripActivity[];
  // All trip activities + every day label — powers the big-map explorer's
  // multi-day view + day picker (the single-day form only needs dayActivities).
  allActivities: TripActivity[];
  tripDayLabels: string[];
  selectedDay: string;
  tripStartDate?: string;
  placement: ActivityPlacement;
  onPlacementChange: (placement: ActivityPlacement) => void;
  onDraftChange: (field: keyof TripActivityDraft, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

// Duration choices for relative placement, in minutes. The server refines the
// dwell from the place category; this is the user's coarse intent.
const RELATIVE_DURATION_OPTIONS: Array<{ label: string; minutes: number }> = [
  { label: '30 min', minutes: 30 },
  { label: '45 min', minutes: 45 },
  { label: '1 hour', minutes: 60 },
  { label: '1.5 hours', minutes: 90 },
  { label: '2 hours', minutes: 120 },
  { label: '3 hours', minutes: 180 },
];

const ActivityEditorModal: React.FC<ActivityEditorModalProps> = ({
  draft,
  editingActivityId,
  destinations,
  dayActivities,
  allActivities,
  tripDayLabels,
  selectedDay,
  tripStartDate,
  placement,
  onPlacementChange,
  onDraftChange,
  onSave,
  onCancel,
}) => {
  // Place picker (REQ-138): searching Google resolves a real place_id, which is
  // what lets the server compute exact travel legs between stops. Free text
  // stays allowed — a custom stop ("meet a friend") just saves without an id.
  const [suggestions, setSuggestions] = React.useState<PlaceSearchResult[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [pickedMeta, setPickedMeta] = React.useState<PlaceSearchResult | null>(null);
  const [previewPlace, setPreviewPlace] = React.useState<PlaceDetails | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = React.useState(false);
  const searchSeqRef = React.useRef(0);
  const contextText = React.useMemo(() => destinations.filter(Boolean).join(', '), [destinations]);
  const placeText = draft.place || '';

  // Big-map explorer: a full-page map with a multi-day picker under it, so the
  // user can browse several days' stops on a large canvas while picking a place.
  const [isBigMapOpen, setIsBigMapOpen] = React.useState(false);
  const [explorerDays, setExplorerDays] = React.useState<string[]>([]);
  const openBigMap = () => {
    setExplorerDays(selectedDay ? [selectedDay] : tripDayLabels.slice(0, 1));
    setIsBigMapOpen(true);
  };
  const handleExplorerSelectDay = (day: string) =>
    setExplorerDays((current) => resolveNextSelectedDays(day, current, tripDayLabels));

  React.useEffect(() => {
    // A linked place is settled — don't search the chosen name again.
    if (draft.placeId || placeText.trim().length < 3) {
      setSuggestions([]);
      setIsSearching(false);
      return undefined;
    }
    const seq = searchSeqRef.current + 1;
    searchSeqRef.current = seq;
    const timer = window.setTimeout(async () => {
      setIsSearching(true);
      // Bias to the map's current centre so results cluster around what the user
      // is looking at — the day's area — and fill the map with nearby options.
      const center = modalMapObj.current?.getCenter?.();
      const bias = center ? { lat: center.lat(), lng: center.lng() } : undefined;
      const results = await searchPlacesByText(placeText, contextText, bias);
      if (searchSeqRef.current !== seq) return; // stale query
      setIsSearching(false);
      setSuggestions(results);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [placeText, draft.placeId, contextText]);

  const handlePlaceInput = (value: string) => {
    onDraftChange('place', value);
    if (draft.placeId) onDraftChange('placeId', ''); // edited text ≠ the linked place
    setPickedMeta(null);
    setPreviewPlace(null);
  };

  const handlePickPlace = (place: PlaceSearchResult) => {
    onDraftChange('place', place.name);
    onDraftChange('placeId', place.id);
    setPickedMeta(place);
    setSuggestions([]);
  };

  const handleUnlinkPlace = () => {
    onDraftChange('placeId', '');
    setPickedMeta(null);
    setPreviewPlace(null);
  };

  // ── Day-context map: today's stops (numbered, joined by a path line) + the
  // candidate stop, so "is this on the way?" is answerable at a glance. Tapping
  // a POI on the map picks it directly (Google click events carry a placeId).
  const modalMapRef = React.useRef<HTMLDivElement | null>(null);
  const modalMapObj = React.useRef<any>(null);
  const dayMarkersRef = React.useRef<any[]>([]);
  const searchMarkersRef = React.useRef<any[]>([]);
  const pathRef = React.useRef<any>(null);
  const candidateMarkerRef = React.useRef<any>(null);
  const dayPositionsRef = React.useRef<Map<string, { lat: number; lng: number }>>(new Map());
  // One-shot guard: true once the map has been auto-framed for this open.
  const hasFittedRef = React.useRef(false);
  const [mapAvailable, setMapAvailable] = React.useState(false);
  const pickFromMapRef = React.useRef(handlePickPlace);
  pickFromMapRef.current = handlePickPlace;

  React.useEffect(() => {
    if (!draft.placeId) {
      setPreviewPlace(null);
      setIsLoadingPreview(false);
      return undefined;
    }

    let cancelled = false;
    const requestId = draft.placeId;
    setIsLoadingPreview(true);

    void fetchPlaceDetailsFromGoogle(modalMapRef.current, draft.placeId, {
      queryFallback: placeText || undefined,
    }).then((details) => {
      if (cancelled || requestId !== draft.placeId) return;
      setPreviewPlace(details);
      setIsLoadingPreview(false);
    });

    return () => {
      cancelled = true;
    };
  }, [draft.placeId, placeText]);

  const fitModalMap = React.useCallback((): boolean => {
    const map = modalMapObj.current;
    if (!map || !window.google?.maps?.LatLngBounds) return false;
    const bounds = new window.google.maps.LatLngBounds();
    let count = 0;
    dayPositionsRef.current.forEach((pos) => { bounds.extend(pos); count += 1; });
    const candidate = candidateMarkerRef.current?.getPosition?.();
    if (candidate) { bounds.extend(candidate); count += 1; }
    if (count === 0) return false;
    if (count === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(13);
    } else {
      map.fitBounds(bounds, 32);
    }
    return true;
  }, []);

  // Frame the map ONCE per open (the initial day context). After that the user
  // owns the viewport — picking a place (map tap or search) or a re-plot must
  // NOT yank them back to the default framing: they often zoom into an area to
  // inspect it before tapping a POI, and refitting would throw that away.
  const maybeInitialFit = React.useCallback(() => {
    if (hasFittedRef.current) return;
    if (fitModalMap()) hasFittedRef.current = true;
  }, [fitModalMap]);

  // Init once per modal open. Sync init + ref guard — an await between map
  // creation and state would let a StrictMode remount deadlock (the sidebar
  // lesson).
  React.useEffect(() => {
    if (modalMapObj.current) { setMapAvailable(true); return undefined; }
    if (!modalMapRef.current || !window.google?.maps?.Map) return undefined;

    const map = new window.google.maps.Map(modalMapRef.current, {
      center: { lat: 38.7223, lng: -9.1393 },
      zoom: 11,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: true,
    });
    modalMapObj.current = map;
    setMapAvailable(true);
    window.setTimeout(() => {
      if (window.google?.maps?.event && modalMapObj.current) {
        window.google.maps.event.trigger(modalMapObj.current, 'resize');
      }
    }, 0);

    // POI tap = pick that place.
    const clickListener = map.addListener('click', async (event: any) => {
      if (!event?.placeId) return;
      event.stop?.(); // suppress Google's own info window
      const details = await fetchPlaceDetailsFromGoogle(modalMapRef.current, event.placeId);
      pickFromMapRef.current({
        id: event.placeId,
        name: details?.name || 'Selected place',
        address: details?.formatted_address,
        rating: details?.rating,
      });
    });

    return () => {
      clickListener?.remove?.();
      dayMarkersRef.current.forEach((marker) => marker.setMap(null));
      dayMarkersRef.current = [];
      searchMarkersRef.current.forEach((marker) => marker.setMap(null));
      searchMarkersRef.current = [];
      pathRef.current?.setMap?.(null);
      pathRef.current = null;
      candidateMarkerRef.current?.setMap?.(null);
      candidateMarkerRef.current = null;
      modalMapObj.current = null;
    };
  }, []);

  // Plot the day's existing stops + the connecting path (straight segments in
  // visit order — an at-a-glance corridor, not turn-by-turn).
  React.useEffect(() => {
    if (!mapAvailable || !modalMapObj.current) return undefined;
    let cancelled = false;

    const plot = async () => {
      const map = modalMapObj.current;
      dayMarkersRef.current.forEach((marker) => marker.setMap(null));
      dayMarkersRef.current = [];
      pathRef.current?.setMap?.(null);
      pathRef.current = null;
      dayPositionsRef.current.clear();

      const stops = dayActivities
        .filter((activity) => activity.id !== editingActivityId)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));

      const orderedPositions: Array<{ lat: number; lng: number }> = [];
      for (let i = 0; i < stops.length; i += 1) {
        const stop = stops[i];
        const geometry = await fetchPlaceGeometryFromGoogle(
          modalMapRef.current,
          stop.placeId || '',
          { queryFallback: `${stop.place || stop.description}${destinations.length ? `, ${destinations.join(', ')}` : ''}` },
        );
        if (cancelled || !modalMapObj.current) return;
        if (!geometry) continue;
        const position = { lat: geometry.lat, lng: geometry.lng };
        dayPositionsRef.current.set(stop.id, position);
        orderedPositions.push(position);
        dayMarkersRef.current.push(new window.google.maps.Marker({
          map,
          position,
          title: stop.description,
          label: { text: String(i + 1), color: '#ffffff', fontWeight: '700' },
        }));
      }

      if (orderedPositions.length > 1 && window.google?.maps?.Polyline) {
        pathRef.current = new window.google.maps.Polyline({
          map,
          path: orderedPositions,
          geodesic: true,
          strokeColor: '#16563f',
          strokeOpacity: 0.55,
          strokeWeight: 3,
        });
      }
      maybeInitialFit();
    };

    void plot();
    return () => { cancelled = true; };
  }, [mapAvailable, dayActivities, editingActivityId, destinations, maybeInitialFit]);

  // Candidate marker follows the picked place.
  React.useEffect(() => {
    if (!mapAvailable || !modalMapObj.current) return undefined;
    let cancelled = false;

    const update = async () => {
      candidateMarkerRef.current?.setMap?.(null);
      candidateMarkerRef.current = null;
      if (!draft.placeId) { maybeInitialFit(); return; }
      const geometry = await fetchPlaceGeometryFromGoogle(
        modalMapRef.current,
        draft.placeId,
        { queryFallback: placeText || undefined },
      );
      if (cancelled || !modalMapObj.current || !geometry) return;
      candidateMarkerRef.current = new window.google.maps.Marker({
        map: modalMapObj.current,
        position: { lat: geometry.lat, lng: geometry.lng },
        title: placeText || 'New stop',
        label: { text: '+', color: '#ffffff', fontWeight: '700' },
        zIndex: 999,
      });
      maybeInitialFit();
    };

    void update();
    return () => { cancelled = true; };
    // placeText intentionally omitted: the marker follows the LINKED place only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapAvailable, draft.placeId, maybeInitialFit]);

  // Search-result pins: drop a tappable blue dot for every current suggestion
  // that has coordinates, so nearby options are visible on the map and can be
  // picked with a tap — not only from the text list. The blue dots stand apart
  // from the numbered day stops and the "+" candidate.
  React.useEffect(() => {
    if (!mapAvailable || !modalMapObj.current) return undefined;
    searchMarkersRef.current.forEach((marker) => marker.setMap(null));
    searchMarkersRef.current = [];
    // A place is already chosen, or nothing to show → no result pins.
    if (draft.placeId || suggestions.length === 0) return undefined;

    const markerIcon = window.google?.maps?.SymbolPath
      ? {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: '#1a73e8',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        }
      : undefined;

    suggestions.forEach((suggestion) => {
      if (!suggestion.location) return;
      const marker = new window.google.maps.Marker({
        map: modalMapObj.current,
        position: suggestion.location,
        title: suggestion.name,
        icon: markerIcon,
        zIndex: 500,
      });
      marker.addListener('click', () => pickFromMapRef.current(suggestion));
      searchMarkersRef.current.push(marker);
    });

    return () => {
      searchMarkersRef.current.forEach((marker) => marker.setMap(null));
      searchMarkersRef.current = [];
    };
  }, [mapAvailable, suggestions, draft.placeId]);

  const previewPhotos = previewPlace?.photos?.slice(0, 3) ?? [];
  const previewName = previewPlace?.name || pickedMeta?.name || draft.place || '';
  const previewAddress = previewPlace?.formatted_address || pickedMeta?.address || '';
  const previewRating = previewPlace?.rating ?? pickedMeta?.rating;
  const previewTripDayHours = previewPlace
    ? getHoursForTripDay(previewPlace, selectedDay, tripStartDate)
    : null;
  const hasSelectedPlace = Boolean(draft.placeId);

  // Kept for the (now rare) inline hint; the handlers below prevent an invalid
  // range from being entered in the first place.
  const hasInvalidTimeRange = Boolean(
    draft.startTime && draft.endTime && !isValidActivityTimeRange(draft.startTime, draft.endTime),
  );

  // True when moving the start slid the end along to keep the visit length — the
  // notice below tells the user, and it clears once they touch the end themselves.
  const [endAutoShifted, setEndAutoShifted] = React.useState(false);

  const handleStartTimeChange = (nextStart: string) => {
    onDraftChange('startTime', nextStart);
    // Never strand the user with an empty/invalid end (which used to silently
    // disable Save): if the new start runs past the end, slide the end to preserve
    // the visit's length instead of clearing it.
    if (nextStart && draft.endTime && !isValidActivityTimeRange(nextStart, draft.endTime)) {
      const prevStart = hhmmToMinutes(draft.startTime);
      const prevEnd = hhmmToMinutes(draft.endTime);
      const durationMin = prevEnd > prevStart ? prevEnd - prevStart : 60;
      const nextEndMin = Math.min(23 * 60 + 59, hhmmToMinutes(nextStart) + durationMin);
      onDraftChange('endTime', minutesToHhmm(nextEndMin));
      setEndAutoShifted(true);
    }
  };

  const handleEndTimeChange = (nextEnd: string) => {
    setEndAutoShifted(false); // the user is setting the end themselves now
    if (!nextEnd) {
      onDraftChange('endTime', '');
      return;
    }

    if (draft.startTime && !isValidActivityTimeRange(draft.startTime, nextEnd)) {
      return;
    }

    onDraftChange('endTime', nextEnd);
  };

  // Relative placement: the day's existing stops, ordered, are the anchors the
  // new one can sit before/after. Only offered when adding to a day that already
  // has a stop — the first stop of a day needs a real start time to anchor from.
  const dayStopsSorted = React.useMemo(
    () => [...dayActivities].sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [dayActivities],
  );
  const canPlaceRelative = !editingActivityId && dayStopsSorted.length > 0;
  const isRelative = placement.mode === 'relative' && canPlaceRelative;
  const anchorLabel = (stop: TripActivity) =>
    `${stop.startTime || '--:--'} · ${stop.description || stop.place || 'Stop'}`;

  const selectPlacementMode = (mode: 'time' | 'relative') => {
    if (mode === 'relative') {
      const anchorId = placement.anchorId || dayStopsSorted[dayStopsSorted.length - 1]?.id || '';
      onPlacementChange({ ...placement, mode, anchorId });
    } else {
      onPlacementChange({ ...placement, mode });
    }
  };

  const hasNameOrPlace = Boolean(draft.description.trim() || placeText.trim());
  // Time never blocks Save: exact mode needs only a name + a start (the end is
  // derived on save if missing); relative mode needs a name + an anchor.
  const canSave = isRelative
    ? hasNameOrPlace && Boolean(placement.anchorId)
    : hasNameOrPlace && Boolean(draft.startTime);

  // Non-blocking heads-up shown under Budget/Type: what ELSE a time change does.
  // `dayActivities` holds the day's server-side stops, so the edited row still
  // carries its original time to diff against the draft.
  const editingOriginal = editingActivityId
    ? dayActivities.find((activity) => activity.id === editingActivityId)
    : undefined;
  const timeChangedFromOriginal = Boolean(
    editingOriginal &&
    (editingOriginal.startTime !== draft.startTime || editingOriginal.endTime !== draft.endTime),
  );
  const otherStopsCount = dayActivities.filter((activity) => activity.id !== editingActivityId).length;
  const timeNotices: string[] = [];
  if (!isRelative && endAutoShifted && draft.endTime) {
    timeNotices.push(`End moved to ${draft.endTime} to keep the visit's length — change it if you'd like.`);
  }
  if (!isRelative && timeChangedFromOriginal && otherStopsCount > 0) {
    timeNotices.push(
      `Saving re-times the ${otherStopsCount === 1 ? 'other stop' : `other ${otherStopsCount} stops`} on this day to fit.`,
    );
  }

  return (
  <div className="v2-trip-activity-modal" role="dialog" aria-modal="true" aria-labelledby="activity-modal-title">
    <div className="v2-trip-activity-modal__backdrop" onClick={onCancel} />
    <section className="v2-trip-activity-modal__panel">
      <header>
        <div>
          <p>Day 3 · Oct 14</p>
          <h2 id="activity-modal-title">{editingActivityId ? 'Edit activity' : 'Add activity'}</h2>
        </div>
        <button type="button" onClick={onCancel} aria-label="Close activity form">×</button>
      </header>

      <div className="v2-trip-day-card__editor">
        <label className="v2-place-search">
          Place
          <span>search or tap the map — an exact place gives exact travel times</span>
          <div className="v2-place-search__input-row">
            <input
              type="text"
              value={placeText}
              onChange={(event) => handlePlaceInput(event.target.value)}
              placeholder="Search a place or type a meeting point"
              autoComplete="off"
            />
            {draft.placeId && (
              <button
                type="button"
                className="v2-place-search__unlink"
                onClick={handleUnlinkPlace}
                aria-label="Unlink place"
              >
                Unlink
              </button>
            )}
          </div>
          {!draft.placeId && (isSearching || suggestions.length > 0) && (
            <div className="v2-place-search__list" role="listbox" aria-label="Place suggestions">
              {isSearching && <span className="v2-place-search__hint">Searching…</span>}
              {!isSearching && suggestions.map((suggestion) => (
                <button
                  type="button"
                  key={suggestion.id}
                  className="v2-place-search__item"
                  onClick={() => handlePickPlace(suggestion)}
                >
                  <strong>{suggestion.name}</strong>
                  <span>
                    {suggestion.rating !== undefined ? `★ ${suggestion.rating.toFixed(1)} · ` : ''}
                    {suggestion.address || ''}
                  </span>
                </button>
              ))}
            </div>
          )}
        </label>
        {!draft.placeId && placeText.trim().length >= 3 && !isSearching && suggestions.length === 0 && (
          <p className="v2-place-search__custom-note">
            Saving as a custom stop — travel times to and from it may be unavailable.
          </p>
        )}
        <div className="v2-activity-map-split">
          <div className="v2-activity-map">
            <div ref={modalMapRef} className="v2-activity-map__canvas" />
            {mapAvailable && (
              <button
                type="button"
                className="v2-activity-map__expand"
                onClick={openBigMap}
                aria-label="Open larger map"
                title="Open larger map"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M8 3H3v5M16 3h5v5M21 16v5h-5M3 16v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>

          <aside
            className={`v2-activity-map-preview ${hasSelectedPlace ? 'v2-activity-map-preview--filled' : 'v2-activity-map-preview--empty'}`}
            aria-label="Selected place preview"
          >
            {!hasSelectedPlace && (
              <p>Select a place from search or the map to preview details here.</p>
            )}
            {hasSelectedPlace && isLoadingPreview && (
              <p className="v2-activity-map-preview__loading">Loading place details…</p>
            )}
            {hasSelectedPlace && !isLoadingPreview && (
              <>
                <h3 className="v2-activity-map-preview__title">{previewName || 'Selected place'}</h3>
                {previewAddress && (
                  <p className="v2-activity-map-preview__address">{previewAddress}</p>
                )}
                {previewRating !== undefined && (
                  <div className="v2-activity-map-preview__rating">
                    <span className="v2-activity-map-preview__stars" aria-hidden>
                      {'★'.repeat(Math.round(previewRating))}
                      {'☆'.repeat(Math.max(0, 5 - Math.round(previewRating)))}
                    </span>
                    <strong>{previewRating.toFixed(1)}</strong>
                    {previewPlace?.user_ratings_total ? (
                      <span>({previewPlace.user_ratings_total.toLocaleString()} reviews)</span>
                    ) : null}
                  </div>
                )}
                {previewTripDayHours && (
                  <div className="v2-activity-map-preview__hours">
                    <p>{previewTripDayHours}</p>
                  </div>
                )}
                {previewPhotos.length > 0 && (
                  <div className="v2-activity-map-preview__photos" aria-label="Place photos">
                    {previewPhotos.map((photo, index) => (
                      <img
                        key={`${draft.placeId}-preview-${index}`}
                        src={photo.getUrl({ maxWidth: 240, maxHeight: 160 })}
                        alt=""
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </aside>
        </div>
        {mapAvailable ? (
          <p className="v2-activity-map__hint">
            Numbered pins are this day&apos;s stops; <strong>+</strong> is your new one. Tap a place on the map to select it.
          </p>
        ) : (
          <p className="v2-activity-map__hint">Map preview unavailable.</p>
        )}
        {canPlaceRelative && (
          <div className="v2-activity-placement-toggle" role="tablist" aria-label="How to time this stop">
            <button
              type="button"
              role="tab"
              aria-selected={!isRelative}
              className={`v2-activity-placement-toggle__btn ${!isRelative ? 'v2-activity-placement-toggle__btn--active' : ''}`}
              onClick={() => selectPlacementMode('time')}
            >
              Exact time
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isRelative}
              className={`v2-activity-placement-toggle__btn ${isRelative ? 'v2-activity-placement-toggle__btn--active' : ''}`}
              onClick={() => selectPlacementMode('relative')}
            >
              Before or after a stop
            </button>
          </div>
        )}

        {isRelative ? (
          <div className="v2-activity-relative">
            <div className="v2-trip-day-card__editor-row">
              <label>
                Position
                <select
                  value={placement.side}
                  onChange={(event) =>
                    onPlacementChange({ ...placement, side: event.target.value as 'before' | 'after' })
                  }
                >
                  <option value="after">After</option>
                  <option value="before">Before</option>
                </select>
              </label>
              <label>
                Stop
                <select
                  value={placement.anchorId}
                  onChange={(event) => onPlacementChange({ ...placement, anchorId: event.target.value })}
                >
                  {dayStopsSorted.map((stop) => (
                    <option key={stop.id} value={stop.id}>{anchorLabel(stop)}</option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              Duration
              <select
                value={String(placement.durationMin)}
                onChange={(event) =>
                  onPlacementChange({ ...placement, durationMin: Number(event.target.value) })
                }
              >
                {RELATIVE_DURATION_OPTIONS.map((option) => (
                  <option key={option.minutes} value={option.minutes}>{option.label}</option>
                ))}
              </select>
            </label>
            <p className="v2-activity-relative__hint">
              We&apos;ll set the time from {placement.side === 'after' ? 'the end of' : 'the start of'}{' '}
              that stop plus travel, and shift the rest of the day to fit.
            </p>
          </div>
        ) : (
          <>
            <div className="v2-trip-day-card__editor-row v2-trip-day-card__editor-row--time">
              <label>
                Start
                <TimeField
                  value={draft.startTime}
                  onChange={handleStartTimeChange}
                />
              </label>
              <label>
                End
                <TimeField
                  value={draft.endTime}
                  onChange={handleEndTimeChange}
                />
              </label>
            </div>
            {hasInvalidTimeRange && (
              <p className="v2-trip-day-card__editor-error" role="alert">
                End time must be after start time.
              </p>
            )}
          </>
        )}
        <label>
          Description
          <span>optional — defaults to the place name</span>
          <input
            type="text"
            value={draft.description}
            onChange={(event) => onDraftChange('description', event.target.value)}
            placeholder="e.g. Visit Pena Palace"
          />
        </label>
        <div className="v2-trip-day-card__editor-row">
          <label>
            Budget
            <span>optional</span>
            <input
              type="text"
              value={draft.budget}
              onChange={(event) => onDraftChange('budget', event.target.value)}
              placeholder="$0"
            />
          </label>
          <label>
            Type
            <span aria-hidden="true">&nbsp;</span>
            <select
              value={draft.category}
              onChange={(event) => onDraftChange('category', event.target.value as TripActivityDraft['category'])}
            >
              <option value="activity">Activity</option>
              <option value="travel">Travel</option>
              <option value="food">Food</option>
            </select>
          </label>
        </div>
        {timeNotices.length > 0 && (
          <div className="v2-trip-day-card__editor-notice" role="status">
            {timeNotices.map((notice) => (
              <p key={notice}>{notice}</p>
            ))}
          </div>
        )}
        <div className="v2-trip-day-card__editor-actions">
          {editingActivityId && (
            <button type="button" className="v2-trip-day-card__editor-cancel" onClick={onCancel}>
              Cancel
            </button>
        )}
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave}
          >
            {editingActivityId ? 'Save changes' : 'Add activity'}
          </button>
        </div>
      </div>
    </section>
    {isBigMapOpen && (
      <MapExplorerModal
        allActivities={allActivities}
        destinations={destinations}
        tripDayLabels={tripDayLabels}
        selectedDays={explorerDays}
        onSelectDay={handleExplorerSelectDay}
        draftPlaceId={draft.placeId}
        placeText={placeText}
        onPickPlace={handlePickPlace}
        onClose={() => setIsBigMapOpen(false)}
      />
    )}
  </div>
  );
};

interface MapExplorerModalProps {
  allActivities: TripActivity[];
  destinations: string[];
  tripDayLabels: string[];
  selectedDays: string[];
  onSelectDay: (day: string) => void;
  draftPlaceId?: string;
  placeText: string;
  onPickPlace: (place: PlaceSearchResult) => void;
  onClose: () => void;
}

// Per-day marker/path colours cycled across the selected days so a multi-day
// view is legible at a glance (day 1 stops vs day 2 stops).
const EXPLORER_DAY_COLORS = ['#16563f', '#b4531f', '#5b3fa8', '#1a73e8', '#0b7a75', '#a3245e'];

// Full-page map explorer: the same day-context map, blown up, with a multi-day
// picker beneath it (same selection behaviour as the trip page). Tapping a POI
// picks it for the new stop — identical to the inline map. Its own second map
// instance (isolated lifecycle); the inline map keeps its single-day view.
const MapExplorerModal: React.FC<MapExplorerModalProps> = ({
  allActivities,
  destinations,
  tripDayLabels,
  selectedDays,
  onSelectDay,
  draftPlaceId,
  placeText,
  onPickPlace,
  onClose,
}) => {
  const bigMapRef = React.useRef<HTMLDivElement | null>(null);
  const bigMapObj = React.useRef<any>(null);
  const stopMarkersRef = React.useRef<any[]>([]);
  const pathsRef = React.useRef<any[]>([]);
  const candidateRef = React.useRef<any>(null);
  const [ready, setReady] = React.useState(false);
  const pickRef = React.useRef(onPickPlace);
  pickRef.current = onPickPlace;

  const dayActivitiesByDay = React.useMemo(() => {
    const map = new Map<string, TripActivity[]>();
    selectedDays.forEach((day) => {
      map.set(
        day,
        allActivities
          .filter((activity) => activity.day === day)
          .sort((a, b) => a.startTime.localeCompare(b.startTime)),
      );
    });
    return map;
  }, [allActivities, selectedDays]);

  // Init the second map once (sync create + ref guard — same StrictMode-safe
  // pattern the inline map uses). POI tap picks that place for the new stop.
  React.useEffect(() => {
    if (bigMapObj.current) { setReady(true); return undefined; }
    if (!bigMapRef.current || !window.google?.maps?.Map) return undefined;

    const map = new window.google.maps.Map(bigMapRef.current, {
      center: { lat: 38.7223, lng: -9.1393 },
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: true,
    });
    bigMapObj.current = map;
    setReady(true);
    window.setTimeout(() => {
      if (window.google?.maps?.event && bigMapObj.current) {
        window.google.maps.event.trigger(bigMapObj.current, 'resize');
      }
    }, 0);

    const clickListener = map.addListener('click', async (event: any) => {
      if (!event?.placeId) return;
      event.stop?.();
      const details = await fetchPlaceDetailsFromGoogle(bigMapRef.current, event.placeId);
      pickRef.current({
        id: event.placeId,
        name: details?.name || 'Selected place',
        address: details?.formatted_address,
        rating: details?.rating,
      });
    });

    return () => {
      clickListener?.remove?.();
      stopMarkersRef.current.forEach((marker) => marker.setMap(null));
      stopMarkersRef.current = [];
      pathsRef.current.forEach((path) => path.setMap(null));
      pathsRef.current = [];
      candidateRef.current?.setMap?.(null);
      candidateRef.current = null;
      bigMapObj.current = null;
    };
  }, []);

  // Plot the selected days' stops (per-day colour + numbered pins + a path per
  // day) and frame them once. Re-runs when the day selection changes.
  React.useEffect(() => {
    if (!ready || !bigMapObj.current) return undefined;
    let cancelled = false;

    const plot = async () => {
      const map = bigMapObj.current;
      stopMarkersRef.current.forEach((marker) => marker.setMap(null));
      stopMarkersRef.current = [];
      pathsRef.current.forEach((path) => path.setMap(null));
      pathsRef.current = [];

      const bounds = window.google?.maps?.LatLngBounds ? new window.google.maps.LatLngBounds() : null;
      let plotted = 0;
      const orderedDays = tripDayLabels.filter((day) => selectedDays.includes(day));

      for (let d = 0; d < orderedDays.length; d += 1) {
        const day = orderedDays[d];
        const color = EXPLORER_DAY_COLORS[d % EXPLORER_DAY_COLORS.length];
        const stops = dayActivitiesByDay.get(day) || [];
        const dayPositions: Array<{ lat: number; lng: number }> = [];
        for (let i = 0; i < stops.length; i += 1) {
          const stop = stops[i];
          const geometry = await fetchPlaceGeometryFromGoogle(
            bigMapRef.current,
            stop.placeId || '',
            { queryFallback: `${stop.place || stop.description}${destinations.length ? `, ${destinations.join(', ')}` : ''}` },
          );
          if (cancelled || !bigMapObj.current) return;
          if (!geometry) continue;
          const position = { lat: geometry.lat, lng: geometry.lng };
          dayPositions.push(position);
          bounds?.extend(position);
          plotted += 1;
          stopMarkersRef.current.push(new window.google.maps.Marker({
            map,
            position,
            title: `${day} · ${stop.description}`,
            label: { text: String(i + 1), color: '#ffffff', fontWeight: '700' },
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 11,
              fillColor: color,
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            },
          }));
        }
        if (dayPositions.length > 1 && window.google?.maps?.Polyline) {
          pathsRef.current.push(new window.google.maps.Polyline({
            map,
            path: dayPositions,
            geodesic: true,
            strokeColor: color,
            strokeOpacity: 0.5,
            strokeWeight: 3,
          }));
        }
      }

      // Reframe to the current day selection (the picker is the primary control
      // here). Picking a POI doesn't re-run this effect — the activities ref is
      // stable — so a tap-to-pick never yanks the view.
      if (bounds && plotted > 0) {
        if (plotted === 1) { map.setCenter(bounds.getCenter()); map.setZoom(14); }
        else { map.fitBounds(bounds, 48); }
      }
    };

    void plot();
    return () => { cancelled = true; };
  }, [ready, dayActivitiesByDay, selectedDays, tripDayLabels, destinations]);

  // The candidate (picked place) marker — a green "+" pin, matching the inline map.
  React.useEffect(() => {
    if (!ready || !bigMapObj.current) return undefined;
    let cancelled = false;
    const update = async () => {
      candidateRef.current?.setMap?.(null);
      candidateRef.current = null;
      if (!draftPlaceId) return;
      const geometry = await fetchPlaceGeometryFromGoogle(bigMapRef.current, draftPlaceId, {
        queryFallback: placeText || undefined,
      });
      if (cancelled || !bigMapObj.current || !geometry) return;
      candidateRef.current = new window.google.maps.Marker({
        map: bigMapObj.current,
        position: { lat: geometry.lat, lng: geometry.lng },
        title: placeText || 'New stop',
        label: { text: '+', color: '#ffffff', fontWeight: '700' },
        zIndex: 999,
      });
    };
    void update();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, draftPlaceId]);

  return (
    <div className="v2-map-explorer" role="dialog" aria-modal="true" aria-label="Map explorer">
      <div className="v2-map-explorer__backdrop" onClick={onClose} />
      <section className="v2-map-explorer__panel">
        <header className="v2-map-explorer__header">
          <h2>Map</h2>
          <button type="button" onClick={onClose} aria-label="Close map">×</button>
        </header>
        <div ref={bigMapRef} className="v2-map-explorer__canvas" />
        <p className="v2-map-explorer__hint">
          Numbered pins are each day&apos;s stops; tap a place to pick it. Choose days below to compare them.
        </p>
        <div className="v2-map-explorer__days" aria-label="Trip days">
          {tripDayLabels.map((day) => {
            const [weekday, dayNumber] = day.split(' ');
            const idx = tripDayLabels.filter((d) => selectedDays.includes(d)).indexOf(day);
            const color = idx >= 0 ? EXPLORER_DAY_COLORS[idx % EXPLORER_DAY_COLORS.length] : undefined;
            return (
              <button
                type="button"
                key={day}
                className={selectedDays.includes(day) ? 'v2-map-explorer__day--active' : undefined}
                style={color ? { borderColor: color } : undefined}
                onClick={() => onSelectDay(day)}
              >
                {color && <span className="v2-map-explorer__day-dot" style={{ background: color }} aria-hidden />}
                <span className="v2-map-explorer__day-label">
                  <span>{weekday}</span>
                  <strong>{dayNumber}</strong>
                </span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
};

function createEmptyActivityDraft(): TripActivityDraft {
  return {
    description: '',
    startTime: '',
    endTime: '',
    place: '',
    budget: '',
    category: 'activity',
  };
}

function parseBudget(budget?: string): number {
  if (!budget) return 0;
  const value = Number(budget.replace(/[^0-9.]/g, ''));
  return Number.isFinite(value) ? value : 0;
}

function parseActivityBudget(budget?: string): CreateActivityRequest['budget'] {
  if (!budget) return undefined;

  const value = Number(budget.replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(value) || value <= 0) return undefined;

  return {
    amount: value,
    currency: budget.includes('€') ? 'EUR' : budget.includes('£') ? 'GBP' : 'USD',
  };
}

export default TripDetailPage;
